import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { decryptPayload } from '@aions/crypto';
import type { DenunciaState, Parecer, AcaoRito, SituacaoAcao, TransitionError } from '@aions/core';
import {
  comiteCompetencia,
  ESTADOS_EM_APURACAO,
  estadoLabelComite,
  prazoDoEstado,
  FERIADOS_NACIONAIS,
} from '@aions/core';
import { apiFetch, logout } from '../lib/auth-client';
import { renderForm } from '../lib/pdf/form-pdf';
import { formsAplicaveis, type FormDescriptor } from '../lib/pdf/forms-registry';
import type { FormInput, MemberInfo } from '../lib/pdf/forms-common';
import { useSession } from '../contexts/SessionContext';
// Trilha 3 — fundação compartilhada (Onda 1): resolveCaseDekAccess (bordas, puro)
// + withCaseDek (carrega/zera a KEK; a lib é dona do DEK e o zera no callback).
import { resolveCaseDekAccess, withCaseDek } from '../lib/case-crypto';
import { kekSessionReason } from '../lib/kek-session-reason';
// P5 (ADR "Fatia 4 destravada") — propagação de acesso client-side (self-heal).
import {
  findMissingMembers,
  rewrapForMissingMembers,
  type PublicKeyEntry,
} from '../lib/members-rewrap';
import { base64ToBytes } from '../lib/bytes';
import { VotosComiteCard, type HumanVerdictRow } from '../components/VotosComiteCard';
import { IaSimuladaBadge, type AiMode } from '../components/IaSimuladaBadge';
import { OitivaPanel } from '../components/OitivaPanel';
import { HistoricoPanel } from '../components/HistoricoPanel';
import { ClassificacoesPanel } from '../components/ClassificacoesPanel';
import { PlanoPanel } from '../components/PlanoPanel';
import { MessageComposer } from '../components/MessageComposer';
import {
  InfoComplementarPanel,
  type InfoComplementarItem,
} from '../components/InfoComplementarPanel';
import { RelatorioPanel } from '../components/RelatorioPanel';
import { RitoV2Panel } from '../components/RitoV2Panel';
import { MembroExternoPanel } from '../components/MembroExternoPanel';
import { PendentesLista, type MembroRotulo } from '../components/PendentesLista';
import {
  Banner,
  StatusDot,
  Badge,
  Button,
  Card,
  Modal,
  Alert,
  Tabs,
  tabId,
  tabPanelId,
  categoriaLabel,
  type BadgeTone,
  type BannerTone,
  type StatusDotTone,
  dataHora,
} from '@aions/ui-primitives';

/**
 * As chaves-alvo do re-wrap DESTE caso = as públicas do comitê + a do membro EXTERNO
 * aprovado, quando existe.
 *
 * 🔴 Existe como função porque a regra tem TRÊS sítios de re-wrap nesta tela (pop-up de
 * propagação, execução da propagação, e a detecção que acende o badge "faltam acesso").
 * O CLAUDE.md registra o defeito de corrigir uma cópia e esquecer as outras — foi
 * literalmente assim que o filtro do self-heal apareceu em quatro lugares em 28/07.
 * Consertar dois de três aqui daria o pior desfecho: o externo apareceria na lista de
 * faltantes e não receberia o wrap, ou receberia sem nunca ter sido oferecido.
 *
 * A pública do externo NÃO vem de `/api/denuncia/public-keys` — aquele endpoint é anônimo
 * e `comite_recurso` está fora dele por decisão travada (ADR-0011). Ela vem no detalhe do
 * caso, por caso, para quem já é leitor legítimo dele.
 */
function comExterno(
  chaves: PublicKeyEntry[],
  data: { membroExterno?: { publicKey: { memberId: string; publicKeyJwk: string } | null } | null },
): PublicKeyEntry[] {
  const pk = data.membroExterno?.publicKey;
  if (!pk) return chaves;
  // Não duplica se por algum motivo já vier na lista — `findMissingMembers` contaria o
  // mesmo alvo duas vezes e o pop-up mostraria o nome repetido.
  if (chaves.some((c) => c.memberId === pk.memberId)) return chaves;
  return [...chaves, { memberId: pk.memberId, publicKeyJwk: pk.publicKeyJwk }];
}

interface CaseDetailResponse {
  caseId: string;
  categoria: string;
  createdWeek: string;
  state: DenunciaState;
  /** `null` quando o gate colegiado de conflito está fechado — ver `teor` abaixo. */
  ciphertext: string | null;
  nonce: string | null;
  /**
   * GATE COLEGIADO DO TEOR (servidor, 2026-08-17) — por que não veio conteúdo.
   *
   * Ler ISTO antes de `resolveCaseDekAccess`: com o gate fechado o servidor também
   * retém `myWrappedPrivateKey`, e a borda de acesso diagnosticaria `sem_keypair` —
   * mandando a pessoa relogar para resolver algo que login nenhum resolve.
   *
   * `faltam` é contagem, nunca ids: quem ainda não atestou não é revelado a quem não
   * pode ver o caso. Opcional no tipo porque worker antigo não manda o campo.
   */
  teor?: {
    liberado: boolean;
    faltam: number;
    cobrados: number;
    motivo: 'conflito_pendente_colegiado' | 'roster_vazio_por_impedimento' | 'roster_vazio' | null;
    /** Leitura por ISENÇÃO DO GESTOR, com o colegiado ainda em aberto. */
    isencaoGestor?: boolean;
  };
  /** Phase 2 legacy placeholder — kept while migration script populates per-member array. */
  dekWrappedByComite?: string | null;
  /** Phase 2.1 D-192 — per-member envelope; canonical going forward. */
  dekWrappedByMembers?: Array<{ memberId: string; wrappedDek: string }> | null;
  /** Trilha 3 Fatia 3 Wave 1 — privada-embrulhada do PRÓPRIO membro autenticado
   *  (cifrada sob a KEK dele). Fonte do material privado pro withMemberDek.
   *  Flag OFF (COMITE_READ_ENABLED=false) → o servidor devolve null. */
  myWrappedPrivateKey?: { ciphertext: string; nonce: string } | null;
  /** P10 (ADR-0018) — bloco cifrado da identificação voluntária (base64 opaco).
   *  null = denúncia anônima. Decifrado no MESMO withCaseDek (2º decryptPayload). */
  identidadeCiphertext?: string | null;
  identidadeNonce?: string | null;
  mockMessages: Array<{
    ulid: string;
    ciphertext: string;
    nonce: string;
    createdWeek: string;
    origin?: string;
  }>;
  infoComplementar: Array<{ ulid: string; ciphertext: string; nonce: string; createdWeek: string }>;
  attachments: Array<{ contentHash: string; nonce: string; sizeBucket: string }>;
  /** Prazos Fase 2 — investigação prorrogada (+30); deriva do audit. */
  investigacaoProrrogada?: boolean;
  /** CAPABILITIES lidas do WORKER no ar (nunca VITE_). */
  capabilities?: {
    /** Divergência 3 — worker projeta se a aba "Mensagens" existe (flag OFF → ausente). */
    messagingEnabled?: boolean;
    /**
     * Situação de cada ação do rito, DERIVADA da state machine pelo worker
     * (`situacaoDasAcoes`, packages/core/.../acoes-permitidas.ts).
     *
     * O worker já publicava isto desde a varredura de 28/07 e ESTA TELA NUNCA LEU:
     * o campo não estava nem tipado aqui. Era a metade compensatória desconectada
     * (§"A CURA JÁ ESTAVA NO REPO" do CLAUDE.md) — o produtor ligado, o consumidor
     * não. Tipar e consumir é o que dispensa as cópias da regra de fase no front.
     *
     * `Partial` de propósito: worker mais antigo (ou fixture de teste) não manda o
     * campo, e o fail-open está documentado em `situacaoNaTela`.
     */
    acoes?: Readonly<Partial<Record<AcaoRito, SituacaoAcao>>>;
  };
  /**
   * Atestações de conflito de interesse — CONTAGEM, nunca IDs (privacidade D-212:
   * o painel sabe QUANTOS faltam, nunca QUEM).
   *
   * ⚠️ `total` são TODOS os `comite_membro` ativos, não os designados ao caso.
   * É por isso que isto é AVISO e não gate: um gate sobre este número esperaria
   * atestação de quem talvez nunca entre no caso, e travaria a Triagem contra o
   * prazo de 5 dias úteis do `IT-CMP-002` (decisão do operador, 11/08).
   */
  conflitos?: { total: number; declarados: number; pendentes: number };
  /**
   * GESTÃO UNIPESSOAL — id do `admin_master` que ASSUMIU este caso, ou `null` se a
   * condução é colegiada. Id opaco de MEMBRO (não-anônimo por desenho, ADR-0001);
   * zero PII de denunciante.
   */
  assumidoUnipessoalPor?: string | null;
  /**
   * TRIAGEM DE CONFLITO (ADR-0025) — o denunciado é membro do comitê?
   *
   * `null` enquanto ESTE leitor não declarou o próprio conflito: saber que a IA apontou
   * alguém antes de declarar enviesaria a própria declaração. Declarar primeiro, ver
   * depois.
   */
  triagemConflito?: {
    membroDenunciadoId: string | null;
    /** A IA não conseguiu analisar. "Sem apontamento" aqui NÃO é "sem conflito". */
    liberadaSemAnalise: boolean;
  } | null;
  /** Phase 8 — progresso do quórum SÓ como contagem (voto cego: nunca quem/teor). */
  quorumProgress?: { voted: number; total: number } | null;
  /**
   * Ids do roster efetivo que ainda NÃO emitiram parecer (decisão do operador
   * 11/08). Revela QUEM falta; NÃO revela o TEOR de voto de ninguém — o parecer
   * alheio segue redigido pelo servidor até o quórum fechar. Resolvido para e-mail
   * pelo mesmo diretório (`GET /api/comite/members`) que a lista de conflito usa.
   */
  pareceresPendentes?: ReadonlyArray<string>;
  /** E-mails dos membros ativos que ainda NÃO classificaram. Enquanto não estiver
   *  vazio, `abrir_instrucao` fica travada (gate 422 no servidor). Já vem isento de
   *  gestão unipessoal — quem calcula a isenção é o worker. */
  triagensPendentes?: ReadonlyArray<string>;
  /** Planos de Investigação CIFRADOS, compartilhados entre o comitê (ADR-0023).
   *  A tela decifra e re-renderiza o PDF; o servidor só transporta bytes. */
  planos?: ReadonlyArray<import('../components/PlanoPanel').PlanoGerado>;
  /** Este leitor conduz o caso SOZINHO (role `admin_master` E marco de assunção).
   *  Resposta AUTORITATIVA do servidor — a tela não recalcula o predicado, senão
   *  passariam a existir duas redações da mesma regra. Isenta as travas colegiadas
   *  que são política de tela. */
  conduzUnipessoal?: boolean;
  /** MEU acesso ao teor. `temAcesso` sai do wrap da DEK, `solicitado` sai da trilha —
   *  independentes de propósito (ver `acessoDoMembro` no core). */
  meuAcesso?: { temAcesso: boolean; solicitado: boolean };
  /** Ids de quem PEDIU acesso a este caso e ainda não foi atendido. Marca o diálogo de
   *  propagação para a concessão ser dirigida a quem pediu, não a quem por acaso está na
   *  lista de quem falta chave. */
  solicitantesAcesso?: ReadonlyArray<string>;
  /** Autorizados pelas DUAS cadeiras e ainda sem a chave — entrega automática, sem diálogo. */
  solicitantesAutorizados?: ReadonlyArray<string>;
  /**
   * MEMBRO EXTERNO deste caso (rota `com_membro_externo`, operador 2026-08-18).
   *
   * `euDecido` é resposta AUTORITATIVA do servidor, pelo mesmo motivo de
   * `conduzUnipessoal`: a regra de quem vota na indicação é a mesma do roster exigível, e
   * recalculá-la aqui criaria uma segunda redação que divergiria no primeiro caso com
   * membro denunciado — exatamente onde esta feature é usada.
   */
  membroExterno?: {
    indicadoId: string | null;
    aprovadoId: string | null;
    aprovaram: ReadonlyArray<string>;
    faltam: ReadonlyArray<string>;
    ultimaReprovada: { externoId: string; porId: string } | null;
    euDecido: boolean;
    /** Pública do externo APROVADO — insumo do re-wrap. Sai por caso e não pelo
     *  `/public-keys` anônimo (ADR-0011). `null` até a aprovação fechar. */
    publicKey: { memberId: string; publicKeyJwk: string } | null;
  } | null;
}

/**
 * O que a tela faz com a situação de uma ação do rito.
 *
 * As três respostas do worker viram três comportamentos DIFERENTES, e a distinção
 * é o ponto todo (ver o cabeçalho de `acoes-permitidas.ts`):
 *
 *   fora_de_fase → APARECE DESABILITADA, dizendo que não é desta fase.
 *   bloqueada    → APARECE DESABILITADA, com o motivo VISÍVEL.
 *   permitida    → ativa.
 *
 * 🔴 `fora_de_fase` ESCONDIA, e a primeira versão desta tela fazia isso. Mudou por
 * um caso real (11/08, `RM8GA48SCMNZPAA017Q5GHEN0B`): o caso estava em `Instrucao`,
 * os 4 botões do rito eram todos `fora_de_fase`, a barra ficou praticamente vazia e
 * o operador procurou o botão de reclassificar sem nada na tela explicando que a
 * janela havia fechado ao avançar da Triagem. Ele chegou a atribuir a ausência ao
 * conflito de interesse — que estava com 4/4 declarados. Esconder tinha resolvido
 * "não oferecer o que o servidor recusa" e criado "a tela não responde por quê".
 *
 * Desabilitado-com-motivo resolve os dois: continua não sendo oferecível (não dá
 * para clicar, não leva à tela de escrita, não falha no submit depois do trabalho
 * feito) E responde a pergunta. O que NUNCA volta é o botão ATIVO fora de fase.
 *
 * 🔴 FAIL-OPEN deliberado quando o campo NÃO VEM (worker antigo, fixture de teste):
 * mostra tudo, que é o comportamento de antes desta mudança. A alternativa
 * (esconder tudo) tiraria em silêncio a capacidade do comitê agir por causa de um
 * rollback de worker — dano muito maior que oferecer um botão que o servidor
 * recusa. É fallback consciente, não descuido: está coberto por teste nos DOIS
 * ramos (com e sem `acoes`), para a ausência não passar por "está tudo liberado".
 *
 * ⚠️ Este helper NÃO redige a regra de fase — ele só traduz a resposta de quem
 * redige. Derivar de `state.kind` aqui seria criar a 15ª cópia, que é exatamente o
 * que o módulo do core existe para impedir.
 */
function situacaoNaTela(
  acoes: Readonly<Partial<Record<AcaoRito, SituacaoAcao>>> | undefined,
  acao: AcaoRito,
  /**
   * Atestações de conflito pendentes no colegiado. Desabilita classificar /
   * reclassificar até fechar — decisão do operador (11/08): um membro impedido não
   * deve moldar a classificação. É trava de UI, não gate de rito: o servidor
   * continua aceitando (não inventamos regra na norma, que é silente sobre ordem).
   *
   * ⚠️ TODOS os call-sites passam este número, inclusive os que a trava não atinge.
   * Parece redundante e não é: quando só o call-site do `classificar_triagem` o
   * passava, a trava tinha DUAS camadas (o `acao ===` aqui + a ausência do argumento
   * lá), e uma mascarava a outra — mutação provou que remover o `acao ===` não
   * quebrava teste nenhum. Com todos passando, a regra vive num lugar só e é
   * testável. É o padrão "redundância que esconde ausência" do CLAUDE.md.
   */
  conflitosPendentes = 0,
  /**
   * Quantos membros ativos ainda NÃO classificaram (`triagensPendentes` do worker).
   * Trava `abrir_instrucao` até fechar — decisão do operador (13/08).
   *
   * ⚠️ Diferente da trava de conflito acima, esta TEM gate autoritativo no servidor
   * (422 `triagem_colegiada_pendente` em `POST /:id/abrir-instrucao`). Aqui é
   * conveniência: evita o membro descobrir no submit. Se um dia o gate do servidor
   * sair, esta linha vira mentira — os dois andam juntos.
   *
   * Vem JÁ ISENTO de gestão unipessoal: quem calcula a isenção é o servidor, e
   * recalculá-la aqui seria a segunda redação da regra.
   */
  triagensPendentes = 0,
  /**
   * O leitor conduz o caso sozinho? Vem do servidor (`conduzUnipessoal`).
   *
   * 🔴 ISENTA as travas COLEGIADAS de tela. Sem isto, o gestor que assumiu um caso
   * ficava com o botão Triagem desabilitado para sempre — a trava de conflito exigia
   * atestação de 5 contas que nunca declarariam naquele caso. Relatado pelo operador
   * em 13/08 e reproduzido no navegador antes da correção.
   *
   * ⚠️ Isenta só o que é COLEGIALIDADE. `fora_de_fase` e `bloqueada` continuam de pé:
   * gestão unipessoal dispensa o colegiado, não a state machine — e é isso que mantém
   * a ampla defesa (oitiva antes da decisão) intacta no modo unipessoal.
   */
  conduzUnipessoal = false,
): {
  readonly mostrar: boolean;
  readonly desabilitado: boolean;
  readonly motivo?:
    | TransitionError
    | 'fora_de_fase'
    | 'conflito_colegiado_pendente'
    | 'triagem_colegiada_pendente';
} {
  const s = acoes?.[acao];
  if (s === undefined) return { mostrar: true, desabilitado: false };
  if (s.situacao === 'fora_de_fase')
    return { mostrar: true, desabilitado: true, motivo: 'fora_de_fase' };
  if (s.situacao === 'bloqueada') return { mostrar: true, desabilitado: true, motivo: s.motivo };
  // PERMITIDA pelo rito — só aqui a trava de conflito entra, e só para a
  // classificação. Nunca sobrepõe um motivo de fase: se já está fora de fase, o
  // motivo verdadeiro é a fase, não o conflito (foi essa confusão que gerou o
  // relato de 11/08).
  // Gestão unipessoal dispensa AS DUAS travas colegiadas abaixo. Um `return` só,
  // antes delas, em vez de um `&& !conduzUnipessoal` em cada: a isenção é a MESMA
  // razão nos dois casos, e repeti-la deixaria a próxima trava colegiada nascer sem
  // ela — que é exatamente como este bug aconteceu.
  if (conduzUnipessoal) return { mostrar: true, desabilitado: false };
  if (acao === 'classificar_triagem' && conflitosPendentes > 0) {
    return { mostrar: true, desabilitado: true, motivo: 'conflito_colegiado_pendente' };
  }
  // Triagem colegiada incompleta trava SÓ `abrir_instrucao` — espelha o gate 422 do
  // servidor. `triagensPendentes` já chega isento de gestão unipessoal.
  if (acao === 'abrir_instrucao' && triagensPendentes > 0) {
    return { mostrar: true, desabilitado: true, motivo: 'triagem_colegiada_pendente' };
  }
  return { mostrar: true, desabilitado: false };
}

/**
 * Botão de ação do rito. NUNCA fica ativo fora de fase; quando indisponível, fica
 * desabilitado e o motivo aparece no HOVER, junto da descrição da etapa.
 *
 * ── Por que o hover fica no WRAPPER, e não no botão (2026-08-13)
 *
 * A versão anterior renderizava o motivo como texto FIXO abaixo do botão, com esta
 * justificativa — que continua **correta** e é o motivo de o desenho atual ser o que
 * é: _"botão desabilitado não recebe evento de ponteiro, então o tooltip nativo não
 * apareceria exatamente quando importa"_. `title` num `<button disabled>` é motivo
 * que não existe.
 *
 * A cura não foi ignorar a restrição, foi movê-la de lugar: quem escuta o ponteiro é
 * o `<span>` que ENVOLVE o botão, e esse recebe hover normalmente mesmo com o filho
 * desabilitado. O tooltip é elemento próprio (CSS `:hover`/`:focus-within` no
 * wrapper), não `title` do navegador.
 *
 * Acessibilidade preservada, e este é o ponto que impede a mudança de ser uma
 * regressão: o texto continua SEMPRE no DOM, ligado por `aria-describedby`, então
 * leitor de tela o anuncia sem depender de hover — que ele não tem. O que mudou foi
 * apenas a visibilidade VISUAL. Não trocar por `title`, e não remover o
 * `aria-describedby` "porque o tooltip já mostra".
 *
 * ── E por que isso conserta o ALINHAMENTO de graça
 *
 * O desalinhamento relatado tinha a mesma raiz: `.acao-nota` era `flex-direction:
 * column`, então botão COM motivo ficava mais alto que botão sem, e a barra de ações
 * virava uma linha de alturas diferentes. Com o texto fora do fluxo (tooltip
 * absoluto), todos os itens da barra voltam a ser só botões, do mesmo tamanho.
 *
 * Código não mapeado no i18n cai CRU na tela (`defaultValue`), de propósito: um
 * rótulo estranho é visível e investigável; uma frase genérica ("indisponível
 * agora") esconderia que falta tradução.
 */
function AcaoBotao({
  situacao,
  acao,
  variant,
  loading,
  onClick,
  children,
}: {
  situacao: ReturnType<typeof situacaoNaTela>;
  /** Identifica a ação para buscar a DESCRIÇÃO DA ETAPA no i18n. Obrigatório: sem
   *  ele o tooltip só teria o motivo do bloqueio, e o botão habilitado — que é a
   *  maioria — não ganharia explicação nenhuma. */
  acao: AcaoRito;
  variant: 'primary' | 'ghost';
  loading?: boolean;
  onClick: () => void;
  children: string;
}): JSX.Element | null {
  const { t } = useTranslation();
  if (!situacao.mostrar) return null;

  const descricao = t(`rito.etapa.${acao}`, { defaultValue: '' });
  const motivo = situacao.desabilitado
    ? t(`rito.bloqueio.${situacao.motivo ?? 'desconhecido'}`, {
        defaultValue: situacao.motivo ?? '',
      })
    : '';

  // Sem nada a dizer, não embrulha: um wrapper vazio só adicionaria um nó e um
  // `aria-describedby` apontando para string vazia, que alguns leitores anunciam
  // como pausa sem conteúdo.
  const botao = (
    <Button
      variant={variant}
      size="sm"
      disabled={situacao.desabilitado}
      {...(loading !== undefined ? { loading } : {})}
      {...(descricao !== '' || motivo !== '' ? { 'aria-describedby': `tip-${acao}` } : {})}
      onClick={onClick}
    >
      {children}
    </Button>
  );
  if (descricao === '' && motivo === '') return botao;

  return (
    <span className="acao-tip">
      {botao}
      {/* `role="tooltip"` + id casando com o `aria-describedby` acima. NÃO usar
          `aria-hidden` aqui: o texto é a única explicação do estado do botão, e
          esconder de AT devolveria o problema que o texto fixo resolvia. */}
      <span className="acao-tip__balao" role="tooltip" id={`tip-${acao}`}>
        {/* O MOTIVO vem primeiro quando existe: é a resposta à pergunta que a pessoa
            está fazendo ("por que não posso clicar?"). A descrição da etapa é
            contexto e vem depois, em peso menor. */}
        {motivo !== '' && <span className="acao-tip__motivo">{motivo}</span>}
        {descricao !== '' && <span className="acao-tip__etapa">{descricao}</span>}
      </span>
    </span>
  );
}

/**
 * Data e hora por extenso (decisão do operador 2026-08-13).
 *
 * Vive aqui e não num util compartilhado por um motivo: o `isoDate` do RitoBoardPage
 * continua servindo os PRAZOS, que o `IT-CMP-002` §6 conta em dias úteis, sem hora.
 * Um formatador único para "instante do ato" e "vencimento de prazo" colapsaria a
 * distinção e sugeriria vencimento por horário que a norma não tem.
 */
/** Sprint 1.1b — pareceres reais vivem nos kinds AvaliacaoEmCurso/AguardandoDecisao/Decisao. */
function extractPareceres(state: DenunciaState): ReadonlyArray<Parecer> {
  if (
    state.kind === 'AvaliacaoEmCurso' ||
    state.kind === 'AguardandoDecisao' ||
    state.kind === 'Decisao'
  ) {
    return state.pareceres;
  }
  return [];
}

// 02.8-06 — conteúdo decifrado da denúncia (8 campos do IntakeWizard). Parse
// DEFENSIVO: JSON inválido OU shape inesperado (denúncia legada) → null → fallback <pre>.
type ConteudoDenuncia = {
  descricao?: unknown;
  quando?: unknown;
  onde?: unknown;
  envolvidos?: unknown;
  identificacaoVoluntaria?: unknown;
  policyVersion?: unknown;
  policyAcceptedAt?: unknown;
};
function parseConteudo(raw: string): ConteudoDenuncia | null {
  try {
    const obj: unknown = JSON.parse(raw);
    // shape-check: objeto não-nulo + ao menos UM campo de conteúdo é string.
    if (
      obj !== null &&
      typeof obj === 'object' &&
      !Array.isArray(obj) &&
      (typeof (obj as ConteudoDenuncia).descricao === 'string' ||
        typeof (obj as ConteudoDenuncia).quando === 'string' ||
        typeof (obj as ConteudoDenuncia).onde === 'string' ||
        typeof (obj as ConteudoDenuncia).envolvidos === 'string')
    ) {
      return obj as ConteudoDenuncia;
    }
    return null; // shape inesperado → fallback
  } catch {
    return null; // parse-fail → fallback
  }
}

type Tab =
  | 'descricao'
  | 'pareceres'
  | 'votos'
  | 'oitivas'
  | 'plano'
  | 'mensagens'
  | 'relatorio'
  | 'audit';

type Voto = 'procedente' | 'improcedente' | 'improcedente_insuficiencia';
/** Tom visual do Badge por voto (sem semântica jurídica). */
function votoTone(voto: Voto): BadgeTone {
  return voto === 'procedente' ? 'success' : voto === 'improcedente' ? 'coral' : 'amber';
}

// Phase 2.5 Plan 03 — Banner IA-Auditora com interceptor real server-side.
// Response shape do POST /api/comite/cases/:id/auditoria-status.
interface AuditoraStatus {
  ok: boolean;
  severity: 'ok' | 'warning' | 'block';
  reason?: string;
  message: string;
  proposedEventType?: 'add_parecer' | 'emit_decisao';
  /** Plan 03 — epoch ms da última validação bem-sucedida (ou null). */
  lastValidationAt?: number | null;
  /** Plan 03 — IA-Auditora indisponível. severity=warning → fail-open
   *  (decisões prosseguem); severity=block → fail-closed (transições travadas). */
  degraded?: boolean;
  /** ADR-0007 — 'simulated' enquanto MockAIClient ativo (badge "IA SIMULADA"). */
  aiMode?: AiMode;
  /** Ids OPACOS dos membros do comitê que ainda NÃO declararam conflito de
   *  interesse. Resolvidos para e-mail via `GET /api/comite/members`. Ausente em
   *  respostas degradadas (503/429) — a lista vem do mesmo cálculo que alimenta
   *  a IA-Auditora, e sem ela não há cálculo. */
  conflitosPendentes?: ReadonlyArray<string>;
  /** Ids do roster que ainda não votaram pelo fechamento das oitivas. Só vem
   *  preenchido na fase `Oitivas`. Vazio sob condução unipessoal. */
  fechamentosPendentes?: ReadonlyArray<string>;
  /** Quem ainda não CLASSIFICOU a triagem. Vem vazio fora da fase de triagem — o
   *  servidor é quem decide se a pergunta se aplica, não a tela. */
  triagensPendentes?: ReadonlyArray<string>;
  /** Quem falta votar na indicação do MEMBRO EXTERNO, e quem é o indicado. */
  externoPendentes?: ReadonlyArray<string>;
  externoIndicadoId?: string | null;
  /** Guia descritivo da etapa — o que o rito é agora e o que falta para avançar.
   *  Derivado de `deriveRitoPendencias` no core (autoridade), nunca redigitado aqui. */
  guia?: {
    stage: string;
    estado: string;
    pendencias: ReadonlyArray<{ code: string; label: string; detail?: string }>;
  } | null;
  /** O leitor conduz o caso sozinho — troca o TOM do painel de cobrança para
   *  descrição. Vem do servidor; a tela não recalcula o predicado. */
  conduzUnipessoal?: boolean;
}

/**
 * AuditoraStatusBanner — bloco visual sempre-no-topo mostrando o veredito
 * processual da IA-Auditora para o caso atual. Renderizado sobre o primitivo
 * <Banner/> (tema dark do comitê). Não interativo — read-only, aria-live polite.
 */
const AUDITORA_TONE = {
  ok: {
    tone: 'brand',
    dot: 'success',
    statusKey: 'ia_auditora.banner.status_ok',
    descKey: 'ia_auditora.banner.ok_description',
  },
  warning: {
    tone: 'amber',
    dot: 'live',
    statusKey: 'ia_auditora.banner.status_warning',
    descKey: 'ia_auditora.banner.warning_description',
  },
  block: {
    tone: 'coral',
    dot: 'coral',
    statusKey: 'ia_auditora.banner.status_block',
    descKey: 'ia_auditora.banner.block_description',
  },
} as const;

function fmtValidationTime(ms: number): string {
  return new Date(ms).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/**
 * Lista, por e-mail, quem ainda não declarou conflito de interesse.
 *
 * Antes o banner dizia que havia pendência e não dizia DE QUEM — o membro lia
 * "regularize" sem ter a quem cobrar, e a informação já existia no servidor.
 *
 * Quando o e-mail não é conhecido (lista de membros ainda não carregada, ou id
 * que não está mais no roster), mostra o **id cru** em vez de omitir a linha:
 * omitir faria "3 pendentes" virar "2 exibidos" em silêncio, e a contagem
 * deixaria de bater com a que a IA-Auditora usou. Mesmo princípio da coluna de
 * usuário da AuditPage — nunca "-" no lugar de um id que existe.
 */
/* 🔴 GENERALIZADO em 2026-08-13. Chamava-se `ConflitosPendentesLista` e servia um
   caso só. Ao acrescentar a lista de "quem não votou pelo fechamento das oitivas", a
   escolha era copiar 20 linhas idênticas ou parametrizar o rótulo — e o CLAUDE.md
   registra o custo da primeira: duas cópias divergem no primeiro ajuste, e aqui o
   comportamento delicado (mostrar id cru em vez de omitir a linha) é justamente o que
   ninguém lembraria de replicar. */
function AuditoraStatusBanner({
  status,
  members,
  t,
  meuId,
  rotuloExternoDe,
}: {
  status: AuditoraStatus | null;
  members: ReadonlyArray<MemberInfo> | null;
  t: ReturnType<typeof useTranslation>['t'];
  /** Quem está lendo. Vem de cima e não de um `useSession()` aqui dentro: este
   *  componente é puro de propósito, e os testes o montam sem provider. */
  meuId?: string;
  /** Rótulo do INDICADO a membro externo, que o `members` do caso não conhece enquanto a
   *  indicação está aberta (ver o 🔴 de `candidatosExterno`). Opcional: sem ele o nome
   *  degrada para o id cru, que é o comportamento anterior — não some da tela. */
  rotuloExternoDe?: (id: string) => string;
}): JSX.Element | null {
  if (!status) return null;

  // Plan 03 — IA indisponível (200 fail-open OU 503/429 fail-closed sintético).
  const degraded = status.degraded === true;
  const blocked = degraded && status.severity === 'block'; // 503/429: transições travadas

  let tone: BannerTone;
  let dot: StatusDotTone;
  let statusLabel: string;
  let body: string;

  if (degraded) {
    tone = blocked ? 'coral' : 'amber';
    dot = blocked ? 'coral' : 'live';
    statusLabel = t('ia_auditora.banner.degraded');
    // fail-open → decisões prosseguem; fail-closed → transições bloqueadas.
    body = blocked
      ? t('ia_auditora.banner.degraded_blocked')
      : t('ia_auditora.banner.degraded_failopen');
  } else {
    const map = AUDITORA_TONE[status.severity];
    tone = map.tone;
    dot = map.dot;
    statusLabel = t(map.statusKey);
    const reasonKey =
      status.reason === 'oitiva_ausente_e_dispensa_nao_justificada'
        ? 'ia_auditora.banner.reason_oitiva_ausente'
        : status.reason === 'conflito_de_interesse_pendente'
          ? 'ia_auditora.banner.reason_conflito_pendente'
          : null;
    // Sob condução unipessoal e sem pendência, o corpo DESCREVE em vez de declarar
    // conformidade genérica. "Nenhuma pendência detectada" é verdadeiro e não diz o que
    // fazer — e quem lê é a única pessoa no caso, sem colega a quem perguntar.
    //
    // ⚠️ Só troca no caminho `ok`. Com `reason` presente (a trava da oitiva, por
    // exemplo) o motivo REAL vence: gestão unipessoal muda o tom do painel, nunca
    // esconde um bloqueio.
    body = reasonKey
      ? t(reasonKey)
      : status.conduzUnipessoal === true
        ? t('ia_auditora.banner.desc_unipessoal')
        : t(map.descKey);
  }

  const lastVal = typeof status.lastValidationAt === 'number' ? status.lastValidationAt : null;
  /** Há ação a mostrar? Decide se o `body` de status ainda tem função. */
  const temAcao = (status.guia?.pendencias.length ?? 0) > 0;
  /** Há impedimento com nome? Idem — o rótulo da lista já diz o que falta. */
  const temPendentes =
    (status.conflitosPendentes?.length ?? 0) > 0 ||
    (status.fechamentosPendentes?.length ?? 0) > 0 ||
    (status.triagensPendentes?.length ?? 0) > 0 ||
    (status.externoPendentes?.length ?? 0) > 0;

  return (
    <Banner
      tone={tone}
      role="status"
      aria-live="polite"
      style={{ marginBottom: 16 }}
      icon={<StatusDot tone={dot} live={!blocked && (degraded || status.severity === 'warning')} />}
      // 🔴 O EYEBROW "AUDITORIA PROCESSUAL EM TEMPO REAL" SAIU: era decoração em
      // caixa alta ocupando a primeira linha, e o rodapé ("Verificação de rito
      // automatizada") já diz o que o painel é — duas linhas para a mesma função,
      // no topo, onde a ação deveria estar.
      //
      // Sobrou o STATUS, subordinado: ele distingue conformidade de pendência, e a
      // COR da borda do banner já carrega o mesmo sinal, então pode ser pequeno.
      title={<span className="auditora-banner__statuslabel">{statusLabel}</span>}
    >
      {/* ── HIERARQUIA DE TEXTO (operador, 2026-08-13) ───────────────────────
          "O que precisa estar destacado é o que deve ser feito no rito / os
          impedimentos."

          A ordem ANTERIOR enterrava justamente isso. O painel abria com o `body`
          — uma frase de STATUS ("Nenhuma pendência de rito detectada", "A
          verificação apontou pendência") — e a AÇÃO vinha depois, no mesmo peso
          visual, disputando espaço com duas listas, um chip âmbar e duas linhas
          de rodapé. Quem batia o olho lia o diagnóstico e não o que fazer.

          Agora: AÇÃO primeiro e em destaque, status como subordinado, procedência
          por último e no menor peso. Três níveis, não seis elementos irmãos. */}

      {/* NÍVEL 1 — O QUE FAZER / O IMPEDIMENTO. É o conteúdo do painel.
          `role="group"` + `aria-label` para leitor de tela anunciar o bloco pelo
          que ele é, em vez de despejar uma lista solta. */}
      {status.guia && status.guia.pendencias.length > 0 && (
        <div
          className="auditora-banner__acao"
          data-testid="auditora-guia"
          role="group"
          aria-label={t('ia_auditora.banner.acao_titulo')}
        >
          {/* Etiqueta, não manchete. O DESTAQUE são os ITENS abaixo: é neles que está
              o que fazer, e era isso que estava em peso de bullet. */}
          <p className="auditora-banner__acao-rotulo">{t('ia_auditora.banner.acao_titulo')}</p>
          <ul className="auditora-banner__acao-lista">
            {status.guia.pendencias.map((pd) => (
              <li key={pd.code}>
                {pd.label}
                {pd.detail !== undefined && ` (${pd.detail})`}
              </li>
            ))}
          </ul>
          {/* 🔴 "Etapa atual do rito: X" SAIU. O cabeçalho do caso, três linhas
              abaixo, já mostra o estado num badge (`ABERTA`, `EM TRIAGEM`…). Repetir
              em prosa era uma linha inteira para zero informação nova. */}
        </div>
      )}

      {/* NÍVEL 1 (continuação) — QUEM está impedindo. Mesma importância da ação:
          é o impedimento concreto, com nome. Fica logo abaixo por ser
          consequência dela. */}
      <PendentesLista
        ids={status.conflitosPendentes ?? []}
        members={members}
        t={t}
        {...(meuId !== undefined ? { meuId } : {})}
        labelKey="ia_auditora.banner.conflito_pendentes_label"
      />
      {/* Quem ainda não votou pelo fechamento das oitivas (operador 13/08). Mesma
          lista, outro rótulo — e some sozinha quando ninguém falta (a função devolve
          null com ids vazio), então não há estado "0 pendentes" a renderizar. */}
      {/* Quem ainda não classificou a triagem. Só aparece na fase em que a pergunta
          existe — o servidor manda [] fora dela, e é ele quem sabe. */}
      <PendentesLista
        ids={status.triagensPendentes ?? []}
        members={members}
        t={t}
        {...(meuId !== undefined ? { meuId } : {})}
        labelKey="ia_auditora.banner.triagem_pendentes_label"
      />
      {/* Quem ainda não votou pelo fechamento das oitivas (operador 13/08). Mesma
          lista, outro rótulo — e some sozinha quando ninguém falta. */}
      <PendentesLista
        ids={status.fechamentosPendentes ?? []}
        members={members}
        t={t}
        {...(meuId !== undefined ? { meuId } : {})}
        labelKey="ia_auditora.banner.fechamento_pendentes_label"
      />
      {/* MEMBRO EXTERNO — enquanto a indicação está de pé é ELA que trava o caso, então
          entra no mesmo bloco das outras pendências, com o nome do indicado no rótulo. */}
      <PendentesLista
        ids={status.externoPendentes ?? []}
        members={members}
        t={t}
        {...(meuId !== undefined ? { meuId } : {})}
        labelKey="ia_auditora.banner.externo_pendentes_label"
        extra={{
          nome:
            status.externoIndicadoId === null || status.externoIndicadoId === undefined
              ? ''
              : (rotuloExternoDe?.(status.externoIndicadoId) ??
                members?.find((m) => m.id === status.externoIndicadoId)?.email ??
                status.externoIndicadoId),
        }}
      />

      {/* 🔴 O `body` SAIU DAQUI (operador: "muita informação").
          Ele repetia o que já estava dito duas vezes: "Conflito de interesse não
          declarado por membro do comitê" ao lado de "Faltam declarar conflito de
          interesse (5)" — e do statusLabel "Pendência processual detectada".
          Três frases para um fato.

          Fica SÓ quando não há mais nada a dizer (nem ação, nem pendentes): aí ele é
          a única informação do painel, e sem ele sobraria um banner mudo. */}
      {!temAcao && !temPendentes && <p className="auditora-banner__status">{body}</p>}

      {/* NÍVEL 3 — PROCEDÊNCIA e recência, no menor peso.
          O chip "IA SIMULADA" saiu daqui (pedido do operador). O que o ADR-0007
          exige — que o rótulo descreva a função real em vez de prometer análise por
          modelo de linguagem — continua cumprido por `powered_by` ("Verificação de
          rito automatizada"), que NÃO diz IA em lugar nenhum.
          ⚠️ O que se perdeu é a frase explícita "não por modelo de linguagem", que
          servia ao cenário do auditor externo (ADR-0007 §Contexto item 1). O chip
          segue vivo na `parecer_ia` — ali há uma RECOMENDAÇÃO, e "isto é regra
          pré-definida" pesa muito mais que num checklist de rito. */}
      {/* Procedência e recência numa LINHA só — eram dois parágrafos empilhados no
          rodapé, ambos no menor peso, dizendo coisas de mesma importância. */}
      <p className="auditora-banner__poweredby">
        {t('ia_auditora.banner.powered_by')}
        {lastVal !== null &&
          ` · ${t('ia_auditora.banner.last_validation_at', {
            time: fmtValidationTime(lastVal),
          })}`}
      </p>
    </Banner>
  );
}

export function CaseDetailPage({
  caseId,
  onBack,
  onTriagem,
  onParecer,
  onDecisao,
}: {
  caseId: string;
  onBack: () => void;
  onTriagem: (id: string) => void;
  onParecer: (id: string) => void;
  onDecisao: (id: string) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const { session, setSession } = useSession();
  const [data, setData] = useState<CaseDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Pass 1 PDF — diretório de membros (fetch lazy: só ao baixar um formulário
  // que precisa nomear membros). null = ainda não buscado.
  const [members, setMembers] = useState<MemberInfo[] | null>(null);
  /**
   * Diretório do POOL DE RECURSO (`GET /cases/:id/candidatos-externo`), só para RÓTULO.
   *
   * 🔴 EXISTE PORQUE O `/members` DO CASO NÃO PODE RESPONDER ISTO, por desenho: ele sai
   * de `exigiveisDoCaso`, que só inclui o externo DEPOIS de aprovado, e o comentário da
   * rota diz literalmente que `comite_recurso` fica de fora. Enquanto a indicação está
   * ABERTA — exatamente quando a tela precisa nomear quem está sendo votado — o id do
   * indicado não tem rótulo em lugar nenhum e caía no fallback de ULID cru: "Falta votar
   * para eleger 01H0j0h5… como membro externo". Relato do operador (2026-08-19).
   *
   * É o MESMO defeito de 17/08 registrado logo abaixo (condição de busca que não
   * acompanhou o consumidor novo), com uma diferença que importa: ali a lista certa já
   * estava sendo buscada e faltava buscá-la sempre; aqui a lista certa é OUTRA. Ampliar
   * o `/members` do caso para incluir `comite_recurso` "resolveria" os dois — e daria o
   * roster de recurso inteiro a quem só precisa de um nome, além de reintroduzir contas
   * de recurso no seletor de delegação e nos alvos de re-wrap, que leem a mesma lista.
   * Duas perguntas, duas fontes (é o que o cabeçalho de `/candidatos-externo` já diz).
   *
   * SÓ para rótulo — nunca alimenta `members`, nunca vira candidato a nada.
   */
  const [candidatosExterno, setCandidatosExterno] = useState<ReadonlyArray<MembroRotulo> | null>(
    null,
  );

  /**
   * Carrega o diretório de membros quando há parecer PENDENTE — sem isto a lista
   * de "quem falta" mostraria ULIDs crus, que é honesto (não omite a linha) e
   * inútil para o operador saber quem cobrar.
   *
   * Segue o padrão já estabelecido pela lista de conflito: o servidor manda IDS e o
   * cliente resolve pelo diretório. Mandar e-mail no payload do CASO seria mais
   * curto e sairia do padrão que mantém PII fora de payload case-scoped.
   *
   * Idempotente: só busca com `members === null`, então não refaz a cada reload do
   * caso nem compete com o fetch lazy dos formulários (mesmo `setMembers`).
   */
  // 🔴 A CONDIÇÃO DE BUSCA SAIU (2026-08-17). Era
  // `(data?.pareceresPendentes?.length ?? 0) > 0` — escrita quando `pareceresPendentes`
  // era o ÚNICO consumidor do diretório. Depois entraram `conflitosPendentes` e
  // `fechamentosPendentes`, que vivem em fases onde NÃO há parecer pendente
  // (Oitivas), e a condição nunca foi atualizada: `members` ficava `null`, o
  // `PendentesLista` caía no fallback de id cru e o painel de fechamento de oitivas
  // exibia ULIDs. Foi o relato do operador.
  //
  // Buscar sem condição desacopla "por que preciso do nome" de "quando carrego a
  // lista" — que é o acoplamento que produziu o bug e produziria de novo no 4º
  // consumidor. Segue idempotente (`members !== null`), e o endpoint é barato:
  // projeção de {id, email, subRole} dos membros ativos.
  useEffect(() => {
    if (members !== null) return;
    let cancelado = false;
    void (async () => {
      try {
        const resp = await apiFetch(`/api/comite/cases/${caseId}/members`);
        if (!resp.ok) return;
        const body = (await resp.json()) as { members: MemberInfo[] };
        if (!cancelado) setMembers(body.members);
      } catch {
        // Silencioso de propósito: sem diretório a lista cai no ID cru, que é o
        // fallback já escolhido — não vale derrubar a tela do caso por causa disso.
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [members]);

  /**
   * Diretório do pool de recurso — mesma forma do efeito acima, e SEM CONDIÇÃO de negócio
   * de propósito.
   *
   * Condicionar a `data?.membroExterno?.indicadoId !== null` é a armadilha que o 🔴 de
   * 17/08 acabou de documentar: a busca dependeria de um consumidor específico e ficaria
   * defasada no próximo (a indicação REPROVADA e a APROVADA também precisam do nome, e
   * ambas vivem em estados onde não há indicação aberta). E há um segundo motivo, este de
   * ordem: `data` chega DEPOIS, então a primeira pintura do banner aconteceria sem o
   * diretório — o ULID apareceria e seria substituído pelo e-mail um instante depois, que
   * é o pior dos dois mundos para quem está lendo a tela.
   *
   * Idempotente e silencioso, pelas mesmas razões: sem diretório o rótulo cai no id cru,
   * que é o fallback já escolhido — não vale derrubar a tela do caso por causa dele.
   */
  useEffect(() => {
    if (candidatosExterno !== null) return;
    let cancelado = false;
    void (async () => {
      try {
        const resp = await apiFetch(`/api/comite/cases/${caseId}/candidatos-externo`);
        if (!resp.ok) return;
        const body = (await resp.json()) as { candidatos: MembroRotulo[] };
        if (!cancelado) setCandidatosExterno(body.candidatos);
      } catch {
        // idem ao efeito acima: degrada para ID cru, nunca derruba a tela.
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [candidatosExterno, caseId]);

  /**
   * id → e-mail para os ids que o MEMBRO EXTERNO traz (indicado, aprovado, reprovado).
   *
   * Une as DUAS fontes na ordem certa: o roster do caso primeiro (o externo aprovado já
   * está lá, e é a fonte canônica de quem participa) e o pool de recurso depois (o
   * indicado ainda não aprovado só existe lá). Devolve o ID CRU quando nenhuma conhece —
   * o fallback declarado do `PendentesLista`: nunca omitir a linha.
   */
  const rotuloExternoDe = (id: string): string =>
    members?.find((m) => m.id === id)?.email ??
    candidatosExterno?.find((c) => c.id === id)?.email ??
    id;

  const [formBusy, setFormBusy] = useState<string | null>(null);
  const [prorrogando, setProrrogando] = useState(false);
  const [assumindo, setAssumindo] = useState(false);
  const [needsConflito, setNeedsConflito] = useState(false);
  const [conflitoSubmitting, setConflitoSubmitting] = useState(false);
  // Parede B — abrir-instrução (Triagem→Instrução). loading + lista de papéis
  // faltantes do 503 de composição (reusa a copy comite.non_operational.*).
  const [abrindoInstrucao, setAbrindoInstrucao] = useState(false);
  const [instrucaoMissing, setInstrucaoMissing] = useState<readonly string[] | null>(null);
  const [tab, setTab] = useState<Tab>('descricao');
  // Phase 2.1 D-193 — decrypted content state.
  const [decryptedJson, setDecryptedJson] = useState<string | null>(null);
  // Trilha 3 Fatia 3 Wave 3 — estado de borda da leitura (substitui o decryptError
  // string legado). Detectado ANTES de decifrar (ADR-0012 §2.3); cada valor mapeia
  // pra uma <Alert> de copy aprovada. sem_keypair é o catch-all calmo (cobre flag
  // OFF). NÃO há 5ª borda.
  /** Envio do pedido de acesso em curso. */
  const [pedindoAcesso, setPedindoAcesso] = useState(false);
  const [decryptBorda, setDecryptBorda] = useState<
    | 'sem_keypair'
    | 'caso_legado'
    | 'sem_acesso'
    | 'falha_decrypt'
    | 'sessao_expirada'
    // 5ª e 6ª bordas (2026-08-17). Não são falha de cripto nem de acesso deste
    // membro — são estado do RITO. Duas e não uma porque a copy diverge no que
    // pedem: `teor_retido` não pede NADA (espere), `roster_vazio` pede recomposição
    // pelo administrador. Colapsá-las obrigaria a escolher o texto por dentro do
    // render, que é onde a mistura de estado e apresentação costuma começar.
    | 'teor_retido'
    | 'teor_retido_roster_vazio'
    | null
  >(null);
  // Sprint 1.1b — Map<memberId, justificativaClara> decifrado no mesmo run do DEK.
  const [decryptedPareceres, setDecryptedPareceres] = useState<Map<string, string>>(new Map());
  /** D5 — informação complementar do denunciante, decifrada. Ver InfoComplementarPanel:
   *  o servidor sempre entregou este campo e nenhuma tela o renderizava. */
  const [decryptedInfo, setDecryptedInfo] = useState<ReadonlyArray<InfoComplementarItem>>([]);
  // P10 (ADR-0018) — identificação voluntária decifrada (só com opt-in). null = anônima.
  const [decryptedIdentidade, setDecryptedIdentidade] = useState<{
    nome?: string;
    cpf?: string;
    contato?: string;
  } | null>(null);
  // ADR-0003 — IA-Analista parecer com textoDenuncia placeholder (Phase 4 wire-up spike).
  const [iaParecer, setIaParecer] = useState<{
    recommendation: string;
    justificativa: string;
    citations: Array<{ source: string; text: string }>;
    confidence: number;
  } | null>(null);
  const [iaLoading, setIaLoading] = useState(false);
  const [iaError, setIaError] = useState<string | null>(null);
  // ADR-0004 — Banner always-on IA-Auditora (UI complementar; interceptor real em Phase 2.5).
  const [auditoriaStatus, setAuditoriaStatus] = useState<AuditoraStatus | null>(null);
  // ADR-0007 — modo da IA (simulated/live) p/ parecer + síntese. O banner lê
  // de auditoriaStatus.aiMode; este estado cobre as superfícies acionadas pelo
  // usuário. Sinal é global (mesmo getAIClient), então qualquer resposta serve.
  const [aiMode, setAiMode] = useState<AiMode | null>(null);
  // P5 — propagação MANUAL de acesso (fallback visível do self-heal silencioso).
  const [propagando, setPropagando] = useState(false);
  const [propagacaoMsg, setPropagacaoMsg] = useState<
    'propagado' | 'nada' | 'falha' | 'colegiado' | 'delegado' | null
  >(null);
  /** Quem ainda NÃO lê este caso — nomeado antes de propagar (operador 14/08). */
  const [faltamAcesso, setFaltamAcesso] = useState<string[]>([]);
  /** Candidatos abertos no pop-up (quem ainda não lê) + a seleção do gestor. */
  /** Candidatos do diálogo. `precisaChave` só é usado no modo DELEGAÇÃO, onde a lista é
   *  o comitê inteiro e alguns já têm wrap — é o que diz a quem ainda gerar chave. */
  const [popupCandidatos, setPopupCandidatos] = useState<Array<
    PublicKeyEntry & { precisaChave?: boolean }
  > | null>(null);
  const [selecionados, setSelecionados] = useState<ReadonlySet<string>>(new Set());
  /** Quem acabou de receber, pelo botão OU pelo self-heal. Separado de `faltamAcesso`
   *  de propósito: sem isto, o self-heal (que roda sozinho no mount e propaga em
   *  silêncio) esvaziaria a lista antes de qualquer clique, e a tela nunca diria o
   *  que aconteceu — o membro veria "todos já têm acesso" sem saber que a propagação
   *  foi feita naquele instante, por ele. */
  const [propagadosAgora, setPropagadosAgora] = useState<string[]>([]);

  // P5 (ADR "Fatia 4 destravada") — propaga o acesso a membros que entraram após o
  // submit. Mesma mecânica do self-heal, mas acionada por botão com feedback. Só um
  // leitor legítimo (access 'ok') propaga; gera os wraps RSA client-side e sobe só
  // ciphertext (o servidor nunca vê a DEK).
  /**
   * Passo 1 — abre o pop-up com quem AINDA NÃO lê o caso, para o gestor escolher.
   *
   * Antes o clique propagava direto a todos os faltantes. Dar acesso ao teor de uma
   * denúncia é ato consequente e irreversível (a trilha é append-only e o wrap não é
   * "desembrulhável"); escolher quem entra tem de ser explícito.
   */
  const abrirPopupPropagacao = async (): Promise<void> => {
    if (!data || !session) return;
    setPropagando(true);
    setPropagacaoMsg(null);
    try {
      const pkResp = await apiFetch('/api/denuncia/public-keys');
      if (!pkResp.ok) {
        setPropagacaoMsg('falha');
        return;
      }
      const publicKeys = comExterno((await pkResp.json()) as PublicKeyEntry[], data);
      const missing = findMissingMembers(publicKeys, data.dekWrappedByMembers);
      setFaltamAcesso(missing.map((m) => m.memberId));

      // 🔴 AQUI, E SÓ AQUI, O ROSTER É O GLOBAL — e a exceção é o ponto (2026-08-18).
      //
      // As telas de caso passaram a usar `/cases/:id/members`, escopado ao caso, para o
      // membro externo parar de ver ULIDs crus sem que ninguém ganhasse o roster inteiro.
      // Este diálogo é a ÚNICA superfície que não cabe nesse escopo, por dois motivos que
      // se somam:
      //
      //  1. SOB CONDUÇÃO o escopo do caso são os CONDUTORES. Delegar é escolher quem
      //     passa a conduzir — com a lista escopada, o gestor só poderia delegar a si
      //     mesmo, e o diálogo abriria com um nome só. Sintoma mudo.
      //  2. O GESTOR (`admin_master`) não é exigível por regra, então não está no escopo
      //     do caso. O re-wrap dirigido procura `adminMaster === true` e não acharia
      //     ninguém — a concessão de chave ao gestor sumiria sem erro nenhum.
      //
      // Quem chega aqui é sempre `comite_membro` (o botão vive atrás do acesso de leitura
      // e o `/members` global segue gateado por esse papel). O membro externo NUNCA passa
      // por este caminho: ele não propaga acesso nem delega condução.
      //
      // ⚠️ Numa VARIÁVEL LOCAL, não no estado. O código anterior fazia `setMembers(...)` e
      // duas linhas abaixo lia `members ?? []` — a leitura pega o valor do closure, ainda
      // o antigo, então na PRIMEIRA abertura o diálogo montava a lista com o roster velho
      // (ou vazio). Salvar no estado continua valendo para nomear o resto da tela; quem
      // decide os candidatos agora é o valor que acabou de chegar.
      let rosterComite: MemberInfo[] = members ?? [];
      try {
        const mr = await apiFetch('/api/comite/members');
        if (mr.ok) {
          rosterComite = ((await mr.json()) as { members: MemberInfo[] }).members;
          setMembers(rosterComite);
        }
      } catch {
        // degrada para ids — falhar aqui não pode impedir a propagação.
      }

      // Nenhum candidato E sem condução a encerrar ⇒ não há pop-up a mostrar.
      if (missing.length === 0 && data.conduzUnipessoal !== true) {
        setPropagacaoMsg('nada');
        return;
      }
      // 🔴 SOB CONDUÇÃO o diálogo é de DELEGAÇÃO, e a lista é o COMITÊ INTEIRO — não só
      // quem falta chave (operador, 2026-08-17: "o gestor escolhe quem vai ter acesso; se
      // escolher dois, esses dois seguem com a denúncia").
      //
      // São perguntas diferentes com listas diferentes:
      //   · fora da condução — "quem ainda NÃO tem a chave?" (conceder acesso);
      //   · sob condução ——— "quem CONTINUA com o caso?" (passar a condução adiante).
      // Oferecer só os faltantes na segunda deixava o gestor sem poder escolher justamente
      // quem já tinha chave, que no acervo real é todo mundo — o diálogo abria vazio.
      //
      // O DENUNCIADO fica fora: a triagem o retirou, e o servidor recusa 403 se ele vier
      // no payload. Oferecer um nome que o servidor rejeita é o defeito que os gates de
      // fase existem para evitar.
      if (data.conduzUnipessoal === true) {
        const denunciado = data.triagemConflito?.membroDenunciadoId ?? null;
        const semChave = new Set(missing.map((m) => m.memberId));
        setPopupCandidatos(
          rosterComite
            .filter((m) => m.id !== denunciado)
            .map((m) => ({
              memberId: m.id,
              // `publicKeyJwk` só é necessário para quem ainda receberá chave; para os
              // demais o campo é inerte (o wrap deles já existe).
              publicKeyJwk: missing.find((x) => x.memberId === m.id)?.publicKeyJwk ?? '',
              precisaChave: semChave.has(m.id),
            })),
        );
        setSelecionados(new Set());
        return;
      }
      setPopupCandidatos(missing);
      // Ninguém pré-marcado: a seleção é do gestor, e marcar todos por padrão
      // reproduziria o comportamento anterior com uma etapa extra de teatro.
      setSelecionados(new Set());
    } catch {
      setPropagacaoMsg('falha');
    } finally {
      setPropagando(false);
    }
  };

  /** Passo 2 — propaga SÓ aos selecionados e devolve o caso ao colegiado. */
  const propagarAcesso = async (): Promise<void> => {
    if (!data || !session) return;
    const access = resolveCaseDekAccess(data, session.user.id);
    if (access.status !== 'ok') {
      setPropagacaoMsg('falha');
      return;
    }
    setPropagando(true);
    setPropagacaoMsg(null);
    try {
      const pkResp = await apiFetch('/api/denuncia/public-keys');
      if (!pkResp.ok) {
        setPropagacaoMsg('falha');
        return;
      }
      const publicKeys = comExterno((await pkResp.json()) as PublicKeyEntry[], data);
      // SÓ os selecionados no pop-up. O `findMissingMembers` continua sendo a fonte de
      // quem PODE receber (é ele que conhece a regra de referência de keypair vigente);
      // a seleção apenas restringe. Filtrar aqui e não no servidor é correto porque o
      // wrap é gerado no cliente — o servidor nunca vê a DEK.
      const missing = findMissingMembers(publicKeys, data.dekWrappedByMembers).filter((m) =>
        selecionados.has(m.memberId),
      );
      // Mesma definição do resto da tela (`conduzoSozinho`), lida aqui do `data` porque
      // este handler não fecha sobre a const do render.
      const delegando = data.conduzUnipessoal === true;
      const wraps =
        missing.length > 0
          ? await withCaseDek(access, (dek) => rewrapForMissingMembers(dek, missing))
          : [];
      // ⚠️ Segue adiante mesmo com `wraps` vazio QUANDO se está delegando: escolher quem
      // continua com o caso é ato próprio, e no acervo real todo mundo já tem chave —
      // sair aqui faria o clique não ter efeito nenhum e sem erro.
      if (wraps.length === 0 && !delegando) {
        setPropagacaoMsg('nada');
        return;
      }

      // 🔴 DOIS ATOS, NESTA ORDEM, e a ordem é o ponto.
      //
      // A chave PRIMEIRO: delegar a quem ainda não consegue abrir o caso o deixaria
      // conduzindo uma denúncia que ele não lê. Se o rewrap falhar, paramos antes de
      // mexer na condução — o estado ruim é "ninguém recebeu nada", não "recebeu a
      // condução sem a chave".
      //
      // Eles são SEPARADOS no servidor de propósito (§`delegar-conducao`): o rewrap
      // transporta ciphertext gerado no browser, a delegação é ato de rito. Fundi-los
      // poria geração de chave no servidor, que é a linha do ADR-0002.
      let recebidos: string[] = [];
      if (wraps.length > 0) {
        const rw = await apiFetch(`/api/comite/cases/${caseId}/rewrap-members`, {
          method: 'POST',
          body: JSON.stringify({ wraps }),
        });
        if (!rw.ok) {
          setPropagacaoMsg('falha');
          return;
        }
        recebidos = ((await rw.json().catch(() => ({}))) as { added?: string[] }).added ?? [];
      }

      let virouColegiado = false;
      if (delegando) {
        const dl = await apiFetch(`/api/comite/cases/${caseId}/delegar-conducao`, {
          method: 'POST',
          body: JSON.stringify({ memberIds: [...selecionados] }),
        });
        if (!dl.ok) {
          setPropagacaoMsg('falha');
          return;
        }
        virouColegiado =
          ((await dl.json().catch(() => ({}))) as { colegiado?: boolean }).colegiado === true;
      }

      setPropagacaoMsg(
        virouColegiado
          ? 'colegiado'
          : delegando
            ? 'delegado'
            : recebidos.length > 0
              ? 'propagado'
              : 'nada',
      );
      setPropagadosAgora(recebidos);
      setPopupCandidatos(null);
      if (recebidos.length > 0) setFaltamAcesso([]);
      // Recarrega sempre que houve efeito: a delegação muda o selo de condução, a
      // visibilidade e as pendências do auditor — não só a lista de quem lê.
      if (recebidos.length > 0 || delegando) await load();
    } catch {
      setPropagacaoMsg('falha');
    } finally {
      setPropagando(false);
    }
  };

  // Pass 1 PDF — gera o formulário CLIENT-SIDE e dispara o download. Fetch de
  // membros é lazy: só busca o diretório quando o formulário precisa nomear
  // membros e ainda não foi carregado. ZERO conteúdo cifrado sai daqui.
  const handleDownloadForm = async (desc: FormDescriptor): Promise<void> => {
    if (!data) return;
    setFormBusy(desc.key);
    try {
      let mem: ReadonlyArray<MemberInfo> = members ?? [];
      if (desc.needsMembers && members === null) {
        const resp = await apiFetch(`/api/comite/cases/${caseId}/members`);
        if (resp.ok) {
          const body = (await resp.json()) as { members: MemberInfo[] };
          mem = body.members;
          setMembers(body.members);
        }
      }
      const input: FormInput = {
        caseId: data.caseId,
        categoria: data.categoria,
        createdWeek: data.createdWeek,
        state: data.state,
        attachments: data.attachments.map((a) => ({
          contentHash: a.contentHash,
          sizeBucket: a.sizeBucket,
        })),
        members: mem,
      };
      const blob = renderForm(desc.build(input));
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${desc.fileBase}-${data.caseId.slice(0, 12)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setFormBusy(null);
    }
  };

  const load = async (): Promise<void> => {
    setError(null);
    setData(null);
    setNeedsConflito(false);
    setDecryptedJson(null);
    setDecryptedIdentidade(null);
    setDecryptBorda(null);
    setDecryptedPareceres(new Map());
    setDecryptedInfo([]);
    // ADR-0009 — o gate biométrico é server-side (selo biometric_verified no JWT,
    // Peça 3). O caso carrega só com o Bearer; o encanamento client-side antigo
    // (X-Biometric-Hash / BiometricContext) foi removido na Peça 6b (F3-A).
    try {
      const resp = await apiFetch(`/api/comite/cases/${caseId}`);
      if (resp.status === 412) {
        setNeedsConflito(true);
        return;
      }
      if (!resp.ok) {
        setError('Erro ao carregar caso.');
        return;
      }
      const json = (await resp.json()) as CaseDetailResponse;
      setData(json);
    } catch {
      setError('Falha de rede.');
    }
  };

  // Prazos Fase 2 — prorrogação +30 da investigação. Ação manual SOFT: o POST só
  // registra o evento de audit (não muda o estado); recarregamos o caso para o
  // badge refletir 60d e o botão sumir.
  /**
   * GESTÃO UNIPESSOAL — assume a condução deste caso.
   *
   * Recarrega o caso no sucesso porque o efeito é do SERVIDOR: quem passa a poder
   * classificar/atribuir é decidido lá, e o `assumidoUnipessoalPor` da projeção é o
   * que faz o selo aparecer. Não há estado local de "assumido" para dessincronizar.
   */
  const handleAssumirUnipessoal = async (): Promise<void> => {
    setAssumindo(true);
    // Limpa o resultado da propagação ANTERIOR. Sem isto, quem devolvia o caso ao
    // colegiado e voltava a assumir ficava com "Acesso propagado. O caso voltou ao
    // comitê" na tela ao lado do selo de condução unipessoal recém-criado — duas
    // afirmações opostas, a segunda verdadeira e a primeira só velha.
    setPropagacaoMsg(null);
    setPropagadosAgora([]);
    try {
      const resp = await apiFetch(`/api/comite/cases/${caseId}/assumir-unipessoal`, {
        method: 'POST',
      });
      if (resp.ok) await load();
    } finally {
      setAssumindo(false);
    }
  };

  const handleProrrogar = async (): Promise<void> => {
    setProrrogando(true);
    try {
      const resp = await apiFetch(`/api/comite/cases/${caseId}/prorrogar-investigacao`, {
        method: 'POST',
      });
      if (resp.ok) await load();
    } finally {
      setProrrogando(false);
    }
  };

  useEffect(() => {
    void load();
  }, [caseId]); // load reads only `caseId` — stable closure

  // ADR-0004 — Poll IA-Auditora no mount e em toda mudança de estado do
  // caso. Read-only (não muta state). Re-dispara quando data atualiza (após
  // declarar conflito, emitir parecer, etc) — mock recalcula severidade.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const resp = await apiFetch(`/api/comite/cases/${caseId}/auditoria-status`, {
          method: 'POST',
        });
        // 200 → status normal OU fail-open degradado (severity=warning, degraded=true).
        if (resp.ok) {
          const body = (await resp.json()) as AuditoraStatus;
          if (!cancelled) {
            setAuditoriaStatus(body);
            setAiMode(body.aiMode ?? null);
          }
          // Carrega a lista de membros SÓ quando há pendência a nomear. Sem isto
          // o banner mostraria ULIDs crus: `members` só era buscado ao gerar um
          // PDF, e quem apenas abre o caso nunca disparava aquele caminho.
          // Falha aqui é silenciosa de propósito — a lista degrada para ids, que
          // continuam corretos e rastreáveis, em vez de sumir da tela.
          if (!cancelled && (body.conflitosPendentes?.length ?? 0) > 0) {
            try {
              const mr = await apiFetch(`/api/comite/cases/${caseId}/members`);
              if (mr.ok && !cancelled) {
                const mb = (await mr.json()) as { members: MemberInfo[] };
                setMembers(mb.members);
              }
            } catch {
              // degrada para id cru
            }
          }
          return;
        }
        // Plan 03 — fail-closed: IA indisponível, transições travadas.
        // 503 (indisponível) ou 429 (rate-limit, Retry-After). Sintetiza estado
        // degradado bloqueante para o banner. NÃO dizer "decisões prosseguindo".
        if (resp.status === 503 || resp.status === 429) {
          const errBody = (await resp.json().catch(() => ({}))) as {
            lastValidationAt?: number | null;
          };
          if (!cancelled) {
            setAuditoriaStatus({
              ok: false,
              severity: 'block',
              message: '',
              degraded: true,
              lastValidationAt: errBody.lastValidationAt ?? null,
            });
          }
          return;
        }
        // Outros erros: silencioso — banner não renderiza.
      } catch {
        // Silencioso — banner simplesmente não renderiza se endpoint falhar.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [caseId, data]);

  /**
   * Lista de membros para NOMEAR o apontado pela triagem.
   *
   * 🔴 `members` só era buscado quando o banner do auditor tinha pendência a nomear. Com
   * o aviso da triagem lendo a mesma lista, um caso sem pendência de conflito mostraria
   * o ULID cru no lugar do e-mail — o mesmo defeito que já mordeu o pop-up de propagação.
   * Falha aqui é silenciosa de propósito: degrada para o id, que continua correto.
   */
  useEffect(() => {
    if (members !== null) return;
    if (data?.triagemConflito?.membroDenunciadoId == null) return;
    let cancelled = false;
    void (async () => {
      try {
        const mr = await apiFetch(`/api/comite/cases/${caseId}/members`);
        if (mr.ok && !cancelled) {
          setMembers(((await mr.json()) as { members: MemberInfo[] }).members);
        }
      } catch {
        // degrada para id
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [data, members]);

  // Trilha 3 — decrypt do membro via case-crypto (Onda 1): resolveCaseDekAccess
  // detecta as bordas ANTES de qualquer cripto, na ordem travada (ADR-0012 §2.3):
  // sem_keypair → caso_legado → sem_acesso → caminho feliz; catch → falha_decrypt.
  // withCaseDek carrega/zera a KEK; o DEK vive só dentro do callback (a lib o zera).
  // Lógica idêntica à anterior — só extraída pra lib compartilhada.
  useEffect(() => {
    if (!data || !session) return;
    // GATE COLEGIADO — PRIMEIRO na cadeia, antes de `resolveCaseDekAccess`. Com o gate
    // fechado o servidor retém também `myWrappedPrivateKey`, então a borda de acesso
    // responderia `sem_keypair`: diagnóstico errado, e a copy dele manda relogar —
    // ação que não destrava nada. A causa é os colegas não terem atestado.
    if (data.teor && !data.teor.liberado) {
      setDecryptBorda(
        data.teor.motivo === 'conflito_pendente_colegiado'
          ? 'teor_retido'
          : 'teor_retido_roster_vazio',
      );
      return;
    }
    // Worker antigo (sem o campo `teor`) com gate fechado é impossível — mas
    // `ciphertext` já é `string | null` no tipo, e o `tsc` cobrou o narrowing aqui.
    // Tratar como `sem_acesso` em vez de `!` : a asserção calaria exatamente o caso
    // em que o servidor decidiu não mandar conteúdo.
    const { ciphertext, nonce } = data;
    if (ciphertext === null || nonce === null) {
      setDecryptBorda('sem_acesso');
      return;
    }
    const access = resolveCaseDekAccess(data, session.user.id);
    if (access.status !== 'ok') {
      setDecryptBorda(access.status);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        await withCaseDek(access, async (dek) => {
          // conteúdo (mesmo AAD=caseId de hoje)
          const json = await decryptPayload(
            base64ToBytes(ciphertext),
            base64ToBytes(nonce),
            dek,
            data.caseId,
          );
          if (!cancelled) setDecryptedJson(json);

          // ── RE-WRAP DIRIGIDO AO GESTOR ÚNICO (operador, 2026-08-17) ──────────
          //
          // "A conta de admin master pode ver os casos criados antes da criação da
          // conta." Isso NÃO é permissão, é cripto: a DEK é embrulhada no submit para
          // as públicas que existiam ali. Conta criada depois não tem wrap, e o
          // servidor é cego — só um leitor com a DEK aberta pode gerar um. Este é o
          // único ponto do código em que essa DEK existe em claro.
          //
          // 🔴 REVERSÃO DECLARADA, e estreita. `propagar-acesso.test.tsx` fixa que
          // "abrir a tela NÃO concede acesso a ninguém" — o self-heal amplo foi
          // removido de propósito. Aqui volta uma versão dirigida: UMA conta (o
          // `admin_master`, que é único por constraint), nunca "todos os faltantes".
          // A diferença importa: o self-heal antigo abria a denúncia para o comitê
          // inteiro por efeito de montagem; este abre para o gestor que responde pelo
          // canal e que já teria acesso a qualquer caso novo.
          //
          // Custo, sem atenuação: abrir um caso antigo passa a conceder a chave dele
          // sem ato explícito de quem abriu. Fica na trilha (`rewrap-members` é
          // append-only e registrado), mas é concessão por efeito colateral.
          //
          // NÃO-FATAL por contrato: qualquer falha aqui é engolida. O teor já está na
          // tela; derrubar a leitura por causa de uma propagação seria trocar um
          // problema de acesso futuro por um de acesso agora.
          //
          // ── E OS AUTORIZADOS PELAS DUAS CADEIRAS (operador, 2026-08-23) ─────────
          //
          // 🔴 O ACESSO AUTORIZADO DEIXOU DE DEPENDER DE UMA TERCEIRA PESSOA.
          //
          // Pedido literal do operador: _"uma vez que o admin master e o administrador TI
          // liberam, o membro poderá ver a denúncia como um membro do comitê normal — não
          // deve ser necessária a propagação por mais ninguém"_. Antes, mesmo com as duas
          // autorizações registradas, alguém tinha de abrir o diálogo "Propagar acesso" e
          // marcar a pessoa na mão — uma terceira decisão, depois de a decisão já ter sido
          // tomada duas vezes. O relato foi exatamente esse: "que chave de leitura é essa?
          // não está sendo exibido nenhuma chave".
          //
          // ⚠️ NÃO é o self-heal amplo que o ADR removeu, e a diferença é a lista. O
          // self-heal antigo re-embrulhava para TODOS os faltantes — abria a denúncia ao
          // comitê inteiro por efeito de montagem, concessão sem ato. Aqui os alvos são
          // `solicitantesAutorizados`: conjunto fechado, e cada nome custou DUAS
          // autorizações de contas distintas, registradas em trilha. É ato explícito sendo
          // executado, não ausência de ato.
          //
          // ⚠️ O SERVIDOR NÃO CONFIA NESTA LISTA. O gate de `rewrap-members` recalcula a
          // autorização e dropa qualquer alvo pendente — mandar um id a mais daqui não
          // concede nada. Fechar só aqui seria "defesa na camada de APRESENTAÇÃO".
          //
          // Residual declarado: a chave chega quando um leitor abre o caso, não no instante
          // do clique de autorizar. Tornar instantâneo exige a via do escrow (servidor
          // re-embrulhando), que é decisão de ADR-0015 pendente com o operador.
          //
          // NÃO-FATAL por contrato: qualquer falha aqui é engolida. O teor já está na
          // tela; derrubar a leitura por causa de uma propagação seria trocar um
          // problema de acesso futuro por um de acesso agora.
          try {
            const gestor = (members ?? []).find((m) => m.adminMaster === true);
            const gestorJaTem = (data.dekWrappedByMembers ?? []).some(
              (w) => w.memberId === gestor?.id,
            );
            // Alvos = o gestor único (re-wrap dirigido de 17/08) ∪ os autorizados pelas duas
            // cadeiras. União num Set porque o gestor pode estar nos dois papéis, e um id
            // repetido faria dois wraps idênticos para a mesma pessoa.
            const alvos = new Set<string>(data.solicitantesAutorizados ?? []);
            if (gestor && !gestorJaTem && gestor.id !== session.user.id) alvos.add(gestor.id);
            alvos.delete(session.user.id); // nunca para si mesmo — já tenho a DEK aberta
            if (alvos.size > 0) {
              const pk = await apiFetch('/api/denuncia/public-keys');
              if (pk.ok) {
                const chaves = (await pk.json()) as PublicKeyEntry[];
                // `findMissingMembers` é a fonte da regra (ele conhece a referência de
                // keypair vigente); aqui só se restringe aos alvos. Quem não estiver entre
                // os faltantes — sem keypair enrolada, ou já com wrap — sai sozinho.
                const alvo = findMissingMembers(chaves, data.dekWrappedByMembers).filter((m) =>
                  alvos.has(m.memberId),
                );
                if (alvo.length > 0) {
                  const wraps = await rewrapForMissingMembers(dek, alvo);
                  if (wraps.length > 0) {
                    await apiFetch(`/api/comite/cases/${caseId}/rewrap-members`, {
                      method: 'POST',
                      body: JSON.stringify({ wraps }),
                    });
                  }
                }
              }
            }
          } catch {
            // Silencioso por contrato — ver o comentário acima.
          }

          // P10 (ADR-0018) — identificação voluntária: 2º decryptPayload na MESMA
          // DEK já aberta, MESMO AAD=caseId. Só quando o denunciante se identificou
          // (campos presentes). Falha isolada NÃO derruba o teor (try/catch próprio).
          if (data.identidadeCiphertext && data.identidadeNonce) {
            try {
              const idJson = await decryptPayload(
                base64ToBytes(data.identidadeCiphertext),
                base64ToBytes(data.identidadeNonce),
                dek,
                data.caseId,
              );
              const parsed: unknown = JSON.parse(idJson);
              if (!cancelled && parsed !== null && typeof parsed === 'object') {
                setDecryptedIdentidade(parsed as { nome?: string; cpf?: string; contato?: string });
              }
            } catch {
              // bloco de identidade corrompido — não bloqueia a leitura do teor.
            }
          }

          // Sprint 1.1b (preservado) — justificativa de cada parecer real, MESMO
          // DEK, mesmo AAD=caseId, DENTRO do callback. Falha em um parecer NÃO
          // bloqueia os outros (placeholder por-parecer). Alimenta decryptedPareceres
          // → aba Pareceres + VotosComiteCard (humanRows) — não-regressão.
          const pareceres = extractPareceres(data.state);
          if (pareceres.length > 0) {
            const decoded = new Map<string, string>();
            for (const p of pareceres) {
              try {
                const just = await decryptPayload(
                  base64ToBytes(p.justificativaCiphertext),
                  base64ToBytes(p.nonce),
                  dek,
                  data.caseId,
                );
                decoded.set(p.memberId, just);
              } catch (e) {
                decoded.set(
                  p.memberId,
                  `[falha ao decifrar: ${e instanceof Error ? e.message : 'unknown'}]`,
                );
              }
            }
            if (!cancelled) setDecryptedPareceres(decoded);
          }

          // D5 — informação complementar do denunciante, MESMA DEK já aberta,
          // MESMO AAD=caseId (é o contrato de /api/status/info-complementar; as
          // mensagens do canal Div3 é que usam messageAad(caseId, ulid) — não
          // confundir, decifrar com a AAD errada falha a tag AES-GCM).
          // Falha em UM item não derruba os outros nem some com ele: vira marcador
          // visível, mesmo padrão dos pareceres logo acima.
          if (data.infoComplementar.length > 0) {
            const infos: InfoComplementarItem[] = [];
            for (const info of data.infoComplementar) {
              try {
                const texto = await decryptPayload(
                  base64ToBytes(info.ciphertext),
                  base64ToBytes(info.nonce),
                  dek,
                  data.caseId,
                );
                infos.push({ ulid: info.ulid, createdWeek: info.createdWeek, texto });
              } catch (e) {
                infos.push({
                  ulid: info.ulid,
                  createdWeek: info.createdWeek,
                  texto: `[falha ao decifrar: ${e instanceof Error ? e.message : 'unknown'}]`,
                });
              }
            }
            if (!cancelled) setDecryptedInfo(infos);
          }
        });

        // ── P5 (ADR-0016) — DETECÇÃO de quem ainda não lê o caso ──────────────────
        //
        // 🔴 ISTO JÁ FOI "SELF-HEAL": propagava sozinho, no mount, para TODOS os
        // faltantes. A propagação automática foi REMOVIDA em 16/08/2026; ficou só a
        // detecção, que alimenta o aviso e o pop-up de seleção.
        //
        // Por que ela não podia ficar, medido no navegador:
        //
        // 1. **Contornava a escolha.** O operador pediu, com todas as letras, um pop-up
        //    "que permita selecionar quais membros vão ter acesso à denúncia". O efeito de
        //    mount concedia a todos antes de qualquer clique — o pop-up decidia sobre um
        //    fato consumado. A trava anterior cobria só a condução unipessoal; no caso
        //    COLEGIADO, que é o comum, a escolha era ignorada.
        // 2. **Concedia a chave a quem a triagem excluiu.** `findMissingMembers` cruza
        //    `/public-keys` (que é AGNÓSTICO ao caso — devolve todo membro com keypair)
        //    com os wraps do caso. Num caso com membro denunciado, o "faltante" incluía a
        //    própria denunciada. Ela seguiria sem VER o caso (o gate é server-side), mas
        //    **wrap entregue não se desfaz** — que é a premissa que motivou o ADR-0025
        //    inteiro. Um caso resolvido a favor dela, ou um bug no gate, e ela decifra.
        //
        // O que se perde: membro que entrou depois do submit continua cego até alguém
        // clicar em propagar. É perda ACEITÁVEL e VISÍVEL — o aviso nomeia quem está de
        // fora, e o botão está ao lado. A alternativa era conceder acesso a denúncia por
        // efeito colateral de abrir uma tela.
        if (!cancelled && data.dekWrappedByMembers && data.dekWrappedByMembers.length > 0) {
          try {
            const pkResp = await apiFetch('/api/denuncia/public-keys');
            if (!cancelled && pkResp.ok) {
              const publicKeys = comExterno((await pkResp.json()) as PublicKeyEntry[], data);
              const missing = findMissingMembers(publicKeys, data.dekWrappedByMembers);
              if (!cancelled) setFaltamAcesso(missing.map((m) => m.memberId));
            }
          } catch {
            // Detecção best-effort — falhar aqui não pode regredir a LEITURA do caso.
          }
        }
      } catch (e) {
        // Regra 1 — a KEK morta (F5, descarte de aba, aba duplicada) NÃO é "falha
        // ao decifrar": é sessão expirada, e quem resolve é o próprio membro. O
        // catch-all antigo mandava falar com o administrador, que é estruturalmente
        // incapaz de ajudar (ADR-0002), escondendo a ação de 5 segundos que cura.
        // `kekSessionReason` devolve null para falha de cripto REAL — essa continua
        // em falha_decrypt, com a copy que aí passa a ser verdade.
        // Sem vazar detalhe técnico (stack/cripto) no texto exibido — só a borda.
        if (!cancelled) {
          setDecryptBorda(kekSessionReason(e) !== null ? 'sessao_expirada' : 'falha_decrypt');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [data, session]);

  const handleSolicitarParecerIA = async (): Promise<void> => {
    setIaLoading(true);
    setIaError(null);
    try {
      const resp = await apiFetch(`/api/comite/cases/${caseId}/parecer-ia`, {
        method: 'POST',
      });
      if (resp.status === 412) {
        setIaError('conflito_pendente');
        return;
      }
      if (!resp.ok) {
        const body = (await resp.json().catch(() => ({}))) as { error?: string };
        setIaError(body.error ?? 'ia_failed');
        return;
      }
      const body = (await resp.json()) as {
        ok: boolean;
        aiMode?: AiMode;
        parecer: {
          recommendation: string;
          justificativa: string;
          citations: Array<{ source: string; text: string }>;
          confidence: number;
        };
      };
      setIaParecer(body.parecer);
      setAiMode(body.aiMode ?? null);
    } catch {
      setIaError('network');
    } finally {
      setIaLoading(false);
    }
  };

  const handleDeclareConflito = async (): Promise<void> => {
    setConflitoSubmitting(true);
    try {
      const resp = await apiFetch(`/api/comite/cases/${caseId}/conflito`, { method: 'POST' });
      if (resp.ok) {
        await load();
      } else {
        setError('Falha ao declarar conflito.');
      }
    } finally {
      setConflitoSubmitting(false);
    }
  };

  // Parede B — Triagem→Instrução (assign_members; o servidor auto-atribui todos os
  // membros ativos). 200 → refetch; 503 → papéis faltantes (composição); 422 →
  // membro externo obrigatório; outros → erro genérico.
  const handleAbrirInstrucao = async (): Promise<void> => {
    setAbrindoInstrucao(true);
    setInstrucaoMissing(null);
    setError(null);
    try {
      const resp = await apiFetch(`/api/comite/cases/${caseId}/abrir-instrucao`, {
        method: 'POST',
      });
      if (resp.ok) {
        await load();
        return;
      }
      if (resp.status === 503) {
        const body = (await resp.json().catch(() => ({}))) as { missing?: readonly string[] };
        setInstrucaoMissing(body.missing ?? []);
        return;
      }
      if (resp.status === 422) {
        const body = (await resp.json().catch(() => ({}))) as { error?: string; detail?: string };
        setError(body.detail ?? body.error ?? 'Falha ao abrir a instrução.');
        return;
      }
      setError('Falha ao abrir a instrução.');
    } catch {
      setError('Falha ao abrir a instrução.');
    } finally {
      setAbrindoInstrucao(false);
    }
  };

  /**
   * ESTE leitor conduz o caso sozinho? UMA definição para a tela inteira.
   *
   * Antes a pergunta era respondida em três lugares por `assumidoUnipessoalPor === eu`
   * — a SEGUNDA redação de um predicado que o servidor já publica pronto em
   * `conduzUnipessoal` (role `admin_master` E marco de assunção). O comentário do
   * próprio payload manda não recalcular, e o bug que ele cita — botão que a tela
   * libera e o servidor recusa — nasce exatamente dessa duplicação.
   */
  const conduzoSozinho = data?.conduzUnipessoal === true;

  /**
   * Membros que TÊM A CHAVE deste caso (wrap da DEK), fora o próprio leitor.
   *
   * 🔴 TER A CHAVE ≠ VER O CASO, e confundir os dois foi o defeito relatado pelo
   * operador. Sob condução unipessoal o caso é invisível para todos menos o condutor
   * (`casoVisivelParaMembro`), então rotular esta lista como "já podem ler" dizia o
   * OPOSTO do selo de condução exibido três linhas acima, na mesma tela.
   *
   * São dois eixos independentes:
   *   · CHAVE — criptográfica, irreversível, é o que o pop-up concede;
   *   · VISIBILIDADE — processual, e é o que o encerramento da condução devolve.
   * Por isso o RÓTULO muda com o modo (ver `propagar_modal_*`): sob condução a lista é
   * "vão voltar a ver ao confirmar"; fora dela é "já leem".
   *
   * Filtra pelo DIRETÓRIO (`members`) em vez de excluir `ia-analista` por nome: a IA
   * não está no diretório, então sai sozinha — e qualquer alvo de wrap futuro que não
   * seja pessoa sai junto, sem alguém precisar lembrar de atualizar uma lista.
   *
   * 🔴 O DENUNCIADO SAI. O wrap dele continua no registro (chave entregue não se desfaz
   * — premissa do ADR-0025), mas ele não volta a ver o caso: a triagem o retirou. Ele
   * na lista faria a tela prometer um retorno que não vai acontecer.
   *
   * O PRÓPRIO LEITOR SAI: a pergunta do diálogo é sobre os OUTROS. Ele já está aqui.
   */
  const outrosComChave = ((): ReadonlyArray<string> => {
    const denunciado = data?.triagemConflito?.membroDenunciadoId ?? null;
    const eu = session?.user.id ?? null;
    const ids = new Set(
      (data?.dekWrappedByMembers ?? [])
        .map((w) => w.memberId)
        .filter((id) => id !== denunciado && id !== eu && (members ?? []).some((m) => m.id === id)),
    );
    return [...ids];
  })();

  /**
   * Pede autorização para ler este caso (operador, 2026-08-19).
   *
   * Recarrega o detalhe em vez de otimizar o estado local: quem responde "já pediu?" é o
   * servidor, pela trilha. Marcar `solicitado` na tela sem confirmar viraria uma tela que
   * afirma um ato que pode não ter sido gravado — e aqui o ato É o produto.
   */
  const handleSolicitarAcesso = async (): Promise<void> => {
    setPedindoAcesso(true);
    try {
      const r = await apiFetch(`/api/comite/cases/${caseId}/solicitar-acesso`, { method: 'POST' });
      if (r.ok) await load();
    } finally {
      setPedindoAcesso(false);
    }
  };

  /**
   * 🔴 VISÃO RESTRITA — membro sem acesso ao teor vê SÓ isto (operador, 2026-08-19).
   *
   * Pedido literal: _"a única visão que ele tem deve ser: Solicite ao administrador a
   * autorização para visualizar esta denúncia e um botão"_. Então o rito inteiro sai da
   * tela — abas, painel de quórum, ações, membro externo.
   *
   * Não é só estética: as abas HOSPEDAM campos de escrita (Mensagens, Oitivas) e os botões
   * do rito praticam atos. Oferecer isso a quem não consegue LER o caso é convidar a
   * decidir sobre o que não se leu — e um parecer emitido às cegas entra na trilha imutável
   * indistinguível de um informado.
   *
   * ⚠️ Fica DEPOIS do `needsConflito`: quem ainda não declarou conflito vê a declaração
   * primeiro. Inverter esconderia o único ato que ele pode praticar aqui.
   *
   * ⚠️ E depende de `meuAcesso` VIR do servidor. Ausente (worker antigo) → a tela cai no
   * comportamento anterior em vez de trancar todo mundo fora. Trancar por falta de
   * informação sobre QUEM está lendo seria pior que mostrar a quem já mostrava.
   */
  const semAcessoAoTeor = data?.meuAcesso?.temAcesso === false;

  return (
    <div>
      <Button variant="ghost" size="sm" onClick={onBack} style={{ marginBottom: 16 }}>
        ← {t('case_detail.back')}
      </Button>

      {/* ADR-0004 — Banner always-on IA-Auditora (sempre antes do conteúdo do caso). */}
      <AuditoraStatusBanner
        status={auditoriaStatus}
        members={members}
        t={t}
        {...(session?.user.id !== undefined ? { meuId: session.user.id } : {})}
        rotuloExternoDe={rotuloExternoDe}
      />

      {needsConflito && (
        <Card padding="md" style={{ borderLeft: '4px solid var(--brand-gold)' }}>
          <h2>{t('case_detail.conflito_required_title')}</h2>
          <p style={{ color: 'var(--text-secondary)', margin: '8px 0 16px' }}>
            {t('case_detail.conflito_required_body')}
          </p>
          <Button
            variant="primary"
            loading={conflitoSubmitting}
            onClick={() => void handleDeclareConflito()}
          >
            {t('case_detail.conflito_declare')}
          </Button>
        </Card>
      )}

      {error === 'biometric_revalidation_required' && (
        <Alert
          variant="warning"
          title={t('case_detail.biometric_expired_title')}
          style={{ marginBottom: 12 }}
        >
          <p style={{ margin: '0 0 12px' }}>{t('case_detail.biometric_expired_body')}</p>
          <Button variant="primary" size="sm" onClick={onBack}>
            {t('case_detail.biometric_expired_relogin')}
          </Button>
        </Alert>
      )}
      {error && error !== 'biometric_revalidation_required' && (
        <Alert variant="error">{error}</Alert>
      )}

      {/* Parede B — 503 de composição ao abrir instrução: papéis faltantes (reusa
          a copy da TriagemPage / ComiteNonOperationalBanner). */}
      {instrucaoMissing !== null && (
        <Alert variant="error" style={{ marginBottom: 12 }}>
          <strong>{t('comite.non_operational.title')}</strong>
          <p style={{ margin: '4px 0 0' }}>
            {t('comite.non_operational.detail', { missing: instrucaoMissing.join(', ') })}
          </p>
        </Alert>
      )}

      {/* 🔴 VISÃO RESTRITA — e é um `return` da árvore, não um `hidden` (19/08).
          Esconder por CSS deixaria as abas montadas: os `useEffect` delas buscariam
          mensagens e oitivas, e os campos de escrita existiriam no DOM. "Não aparece" e
          "não existe" são coisas diferentes quando o que está do outro lado é o teor de
          uma denúncia. Ver `semAcessoAoTeor`. */}
      {data && semAcessoAoTeor && (
        <Card padding="md" data-testid="acesso-restrito">
          <h2 style={{ marginTop: 0 }}>{t('case_detail.acesso_restrito_title')}</h2>
          <p style={{ color: 'var(--text-secondary)' }}>{t('case_detail.acesso_restrito_body')}</p>
          {data.meuAcesso?.solicitado === true ? (
            <Alert variant="info" role="status" data-testid="acesso-solicitado">
              {t('case_detail.acesso_restrito_enviada')}
            </Alert>
          ) : (
            <Button
              variant="primary"
              loading={pedindoAcesso}
              disabled={pedindoAcesso}
              data-testid="acesso-solicitar"
              onClick={() => void handleSolicitarAcesso()}
            >
              {t('case_detail.acesso_restrito_acao')}
            </Button>
          )}
        </Card>
      )}

      {data && !semAcessoAoTeor && (
        <Card>
          {/* GESTÃO UNIPESSOAL — selo permanente enquanto o caso está sob condução de
          uma pessoa. Não é decoração: é a primeira coisa que um auditor procura, e
          esconder isso da tela enquanto a trilha registra seria o inverso do que a
          decisão pediu ("o sistema precisa saber que é uma pessoa só").

          Fica ACIMA do conteúdo, não numa aba: quem abre o caso tem de ver a
          natureza da condução antes de ler o teor. */}
          {(data.assumidoUnipessoalPor ?? null) !== null && (
            <Alert variant="warning" role="note" style={{ marginBottom: 12 }}>
              <strong>
                {t('case_detail.unipessoal_titulo', { defaultValue: 'Condução unipessoal' })}
              </strong>
              <p style={{ margin: '4px 0 0' }}>
                {t('case_detail.unipessoal_corpo', {
                  defaultValue:
                    'Este caso é conduzido por uma pessoa, sem colegialidade. Cada ato fica registrado como unipessoal no log imutável. A oitiva continua obrigatória antes da decisão.',
                })}
              </p>
            </Alert>
          )}

          <div className="case-head">
            <div className="case-head__titles">
              {/* Título humano (a categoria) como headline; o caseId truncado vira
                  etiqueta mono secundária e discreta (design v2). */}
              <h1 className="case-head__title">{categoriaLabel(t, data.categoria)}</h1>
              {/* ID COMPLETO (pedido do operador 13/08). Truncado em 16 chars ele
                  não servia para o uso real — casar o caso com o protocolo do
                  denunciante, com o export judicial e com a trilha. São 26
                  símbolos; cabe. */}
              <span className="case-head__id">{data.caseId}</span>
              <div className="case-head__badges">
                <Badge tone="brand">{estadoLabelComite(data.state.kind)}</Badge>
                {comiteCompetencia(data.categoria) !== null && (
                  <Badge tone="info">
                    {t('case_detail.competencia_label')}:{' '}
                    {t(`competencia.${comiteCompetencia(data.categoria)}`)}
                  </Badge>
                )}
                {(() => {
                  // SLA soft (Triagem/Investigação/Retorno) — alerta derivado, nunca bloqueia.
                  // Investigação usa o flag de prorrogação (60d em vez de 30d).
                  const prazo = prazoDoEstado(
                    data.state,
                    Date.now(),
                    data.investigacaoProrrogada ?? false,
                    FERIADOS_NACIONAIS,
                  );
                  if (prazo === null) return null;
                  const tone: BadgeTone =
                    prazo.status === 'vencido'
                      ? 'coral'
                      : prazo.status === 'vencendo'
                        ? 'amber'
                        : 'neutral';
                  const texto =
                    prazo.status === 'no_prazo'
                      ? t('prazo.no_prazo')
                      : prazo.status === 'vencendo'
                        ? t('prazo.vence_em', { dias: prazo.diasRestantes })
                        : t('prazo.vencido_ha', { dias: Math.abs(prazo.diasRestantes) });
                  return (
                    <Badge tone={tone}>
                      {t('prazo.label')}: {texto}
                    </Badge>
                  );
                })()}
              </div>
            </div>
            {/* UM CTA primário dourado (contextual ao estado) + ações secundárias
                ghost, de menor peso — não 5 botões no mesmo nível (design v2). */}
            <div className="case-head__actions">
              {/* 🔴 AS AÇÕES DO RITO SAEM DA PROJEÇÃO DO WORKER, não de `state.kind`
                  escrito à mão aqui. O que havia antes:

                    • "Triagem" e "Parecer": botões INCONDICIONAIS — apareciam em
                      toda fase, inclusive onde o servidor recusa;
                    • "Decisão": primário em TODO estado que não fosse Triagem
                      (Aberta, Instrução, Oitivas, Encerrada…) E um SEGUNDO botão
                      "Decisão" que aparecia justamente `kind === 'Triagem'`;
                    • "Abrir instrução": em toda a fase Triagem, inclusive NÃO
                      classificada, onde `abrir_instrucao` é fora de fase
                      (`triagem_nao_classificada` — designar comitê antes de
                      classificar é etapa fora de ordem).

              {/* 🔴 ORDEM = SEQUÊNCIA DO RITO (operador, 2026-08-13):
                  Triagem → Instrução → Parecer → Decisão.

                  A ordem ANTERIOR era por PESO VISUAL — os dois CTAs primários
                  (dourados) primeiro, os dois ghost depois — e saía
                  "Abrir instrução, Decisão, Triagem, Parecer". Agrupava por cor e
                  embaralhava o processo: o primeiro botão da barra era o SEGUNDO ato do
                  rito, e o último era o terceiro.

                  A justificativa antiga continua válida no que ela afirmava, e é por isso
                  que a troca é segura: `abrir_instrucao` e `emitir_decisao` são DISJUNTOS
                  na matriz de fases (só Triagem-classificada / só AguardandoDecisao),
                  então nunca há dois dourados ativos ao mesmo tempo — intercalá-los entre
                  os ghost não cria competição de CTA. O que se perde é o agrupamento por
                  cor; o que se ganha é a barra ensinando o processo.

                  O botão ASSUMIR SOZINHO saiu daqui (ver "propagar acesso", na aba
                  Descrição): não é ato do rito, é ato de CONDUÇÃO, e ficava no meio da
                  sequência sem pertencer a ela. */}
              <AcaoBotao
                situacao={situacaoNaTela(
                  data.capabilities?.acoes,
                  'classificar_triagem',
                  data.conflitos?.pendentes ?? 0,
                  data.triagensPendentes?.length ?? 0,
                  data.conduzUnipessoal === true,
                )}
                acao="classificar_triagem"
                variant="ghost"
                onClick={() => onTriagem(caseId)}
              >
                Triagem
              </AcaoBotao>
              <AcaoBotao
                situacao={situacaoNaTela(
                  data.capabilities?.acoes,
                  'abrir_instrucao',
                  data.conflitos?.pendentes ?? 0,
                  data.triagensPendentes?.length ?? 0,
                  data.conduzUnipessoal === true,
                )}
                acao="abrir_instrucao"
                variant="primary"
                loading={abrindoInstrucao}
                onClick={() => void handleAbrirInstrucao()}
              >
                Instrução
              </AcaoBotao>
              <AcaoBotao
                situacao={situacaoNaTela(
                  data.capabilities?.acoes,
                  'emitir_parecer',
                  data.conflitos?.pendentes ?? 0,
                  data.triagensPendentes?.length ?? 0,
                  data.conduzUnipessoal === true,
                )}
                acao="emitir_parecer"
                variant="ghost"
                onClick={() => onParecer(caseId)}
              >
                Parecer
              </AcaoBotao>
              <AcaoBotao
                situacao={situacaoNaTela(
                  data.capabilities?.acoes,
                  'emitir_decisao',
                  data.conflitos?.pendentes ?? 0,
                  data.triagensPendentes?.length ?? 0,
                  data.conduzUnipessoal === true,
                )}
                acao="emitir_decisao"
                variant="primary"
                onClick={() => onDecisao(caseId)}
              >
                Decisão
              </AcaoBotao>
              {/* Prorrogar investigação (+30) — só na fase de investigação e se
                  ainda não prorrogada. SOFT: muda só o prazo exibido. */}
              {['Instrucao', 'Oitivas', 'AvaliacaoEmCurso', 'AguardandoDecisao'].includes(
                data.state.kind,
              ) &&
                data.investigacaoProrrogada !== true && (
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={prorrogando}
                    onClick={() => {
                      void handleProrrogar();
                    }}
                  >
                    {prorrogando ? t('prazo.prorrogando') : t('prazo.prorrogar')}
                  </Button>
                )}
            </div>
          </div>

          {/* Faixa de quórum X/4 + aviso de voto cego. Só quando o WORKER devolve
              quorumProgress (rito v2 ligado) — inerte hoje (flags OFF). Follow-up:
              deduplicar com a faixa própria do RitoV2Panel quando o rito v2 for live. */}
          {data.quorumProgress &&
            (() => {
              const { voted, total } = data.quorumProgress;
              const completo = voted >= total;
              return (
                <Banner
                  tone="info"
                  className="quorum-band"
                  title={t('rito_v2.quorum_progress', { voted, total })}
                >
                  {completo ? t('rito_v2.quorum_completo') : t('rito_v2.quorum_voto_cego')}
                </Banner>
              );
            })()}

          {/* Pass 1 PDF — formulários de apuração aplicáveis ao estado do caso.
              Gerados client-side; conteúdo cifrado nunca sai daqui. */}
          <div style={{ marginTop: 12 }}>
            <div
              style={{
                fontSize: '0.8rem',
                fontWeight: 600,
                color: 'var(--color-text-secondary)',
                marginBottom: 6,
              }}
            >
              {t('formularios.titulo')}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {formsAplicaveis(data.state, data.attachments.length).map((desc) => (
                <Button
                  key={desc.key}
                  variant="ghost"
                  size="sm"
                  disabled={formBusy !== null}
                  onClick={() => {
                    void handleDownloadForm(desc);
                  }}
                >
                  {formBusy === desc.key
                    ? t('formularios.gerando')
                    : `↓ ${t(`formularios.${desc.key}`)}`}
                </Button>
              ))}
            </div>
          </div>

          {/* MEMBRO EXTERNO — indicação, votação e desfecho. Renderiza acima do painel
              do rito de propósito: enquanto a indicação está aberta, ela é o que trava o
              caso, e enterrá-la abaixo do quórum faria a pessoa procurar por que o rito
              não anda. */}
          <MembroExternoPanel
            caseId={data.caseId}
            externo={data.membroExterno ?? null}
            rotuloDe={rotuloExternoDe}
            {...(session?.user.id !== undefined ? { meuId: session.user.id } : {})}
            onChanged={() => void load()}
          />

          {/* Painel do rito colegiado (ADR-0014, F3 — rito único, sempre renderiza;
              o próprio painel não renderiza nada em estados fora do rito). */}
          <RitoV2Panel
            caseId={data.caseId}
            state={data.state}
            quorumProgress={data.quorumProgress ?? null}
            meuId={session?.user.id ?? ''}
            pendentes={(data.pareceresPendentes ?? []).map((id) => ({
              id,
              // Sem e-mail conhecido, mostra o ID CRU — nunca omite a linha. Omitir
              // faria "3 pendentes" virar "2 exibidos" em silêncio e a lista deixaria
              // de bater com a contagem do quórum. Mesmo princípio da
              // ConflitosPendentesLista e da coluna de usuário da AuditPage.
              label: members?.find((m) => m.id === id)?.email ?? id,
            }))}
            onChanged={() => void load()}
          />

          <Tabs<Tab>
            aria-label={t('case_detail.tabs_label')}
            value={tab}
            onValueChange={setTab}
            // 🔴 ORDEM = SEQUÊNCIA DO RITO (operador, 2026-08-13):
            // Descrição → Mensagens → Oitivas → Pareceres → Votos → Histórico.
            //
            // A ordem anterior era Descrição, Pareceres, Votos, Oitivas — colocava o
            // PARECER (penúltimo ato) antes da OITIVA, que é pré-requisito dele: sem
            // oitiva não há decisão (ampla defesa, Lei 14.457/2022 art. 5º). As abas
            // sugeriam um caminho que a state machine recusa.
            //
            // `Mensagens` sobe para o 2º lugar porque é a continuação da Descrição — as
            // duas são o que o DENUNCIANTE disse, e estavam nas pontas opostas da fila.
            //
            // `Histórico` fica por último de propósito: é a trilha do que já aconteceu,
            // não uma etapa a percorrer. As condicionais (`plano`, `relatorio`) ficam
            // onde o rito as usa — investigação e decisão.
            tabs={[
              { value: 'descricao', label: t('case_detail.tab_descricao') },
              // Divergência 3 — aba "Mensagens" só existe com a flag ligada (worker).
              ...(data.capabilities?.messagingEnabled
                ? [{ value: 'mensagens' as Tab, label: t('case_detail.tab_mensagens') }]
                : []),
              { value: 'oitivas', label: t('case_detail.tab_oitivas') },
              // Plano — só nas fases de investigação, que é onde as oitivas acontecem.
              ...(ESTADOS_EM_APURACAO.has(data.state.kind)
                ? [{ value: 'plano' as Tab, label: t('case_detail.tab_plano') }]
                : []),
              { value: 'pareceres', label: t('case_detail.tab_pareceres') },
              { value: 'votos', label: t('case_detail.tab_votos') },
              // Relatório — só na fase Decisão, depois dos votos.
              ...(data.state.kind === 'Decisao'
                ? [{ value: 'relatorio' as Tab, label: t('case_detail.tab_relatorio') }]
                : []),
              { value: 'audit', label: t('case_detail.tab_audit') },
            ]}
          />

          {tab === 'descricao' && (
            <div
              role="tabpanel"
              id={tabPanelId('descricao')}
              aria-labelledby={tabId('descricao')}
              tabIndex={0}
            >
              {/* Trilha 3 Fatia 3 Wave 3 — as 4 bordas da leitura. Alert variant
                  info/warning (NUNCA error/critical vermelho); role="status" +
                  aria-live="polite" (sobrescreve o role="alert" default do Alert
                  via {...rest}). Copy aprovada verbatim (i18n). */}
              {decryptBorda && (
                <Alert
                  variant={
                    // Regra do tom (revista 2026-07-28): âmbar = AÇÃO NECESSÁRIA do
                    // membro; info neutra = NADA A FAZER. A regra antiga era "erro
                    // real × estado esperado", que agrupava mal: sem_keypair e
                    // caso_legado são estados em que ele não pode fazer nada (neutro,
                    // correto), mas sessao_expirada EXIGE ação dele — azul lê como
                    // "para sua informação" e ele concluiria que a tela quebrou. E aí
                    // aperta F5, que é a causa do bug. Não induzir o gatilho.
                    decryptBorda === 'falha_decrypt' ||
                    decryptBorda === 'sessao_expirada' ||
                    // Roster vazio EXIGE ação (recompor pelo admin) — âmbar. O
                    // `teor_retido` simples não exige nada deste membro — info.
                    decryptBorda === 'teor_retido_roster_vazio'
                      ? 'warning'
                      : 'info'
                  }
                  role="status"
                  aria-live="polite"
                  title={t(`case_detail.${decryptBorda}_title`)}
                  style={{ marginBottom: 12 }}
                >
                  {/* `faltam`/`cobrados` só são consumidos pela copy de `teor_retido`;
                      passar sempre evita um segundo `t()` condicional. Tom INFO de
                      propósito: chegar até aqui exige ter declarado o próprio conflito
                      (o `requireConflitoDeclarado` barra o GET antes), então quem lê
                      isto não tem ação nenhuma a tomar — quem falta é outro. */}
                  {t(`case_detail.${decryptBorda}_body`, {
                    faltam: data?.teor?.faltam ?? 0,
                    cobrados: data?.teor?.cobrados ?? 0,
                  })}
                  {/* Tela que diz o que fazer tem que deixar fazer. O aviso de
                      descarte vem JUNTO: esta página parecia ser só leitura, mas
                      HOSPEDA campos de escrita nas abas (Mensagens, Oitivas) — sair
                      daqui descarta o que estiver digitado lá. A redação é de
                      CONSEQUÊNCIA, não instrução, então vale com ou sem rascunho. */}
                  {decryptBorda === 'sessao_expirada' && (
                    <>
                      <div style={{ marginTop: 6 }}>
                        {t('case_detail.sessao_expirada_descarte')}
                      </div>
                      <div style={{ marginTop: 10 }}>
                        <Button
                          variant="primary"
                          size="sm"
                          data-testid="borda-entrar-novamente"
                          onClick={() => {
                            void (async () => {
                              await logout();
                              setSession(null);
                            })();
                          }}
                        >
                          {t('case_detail.sessao_expirada_acao')}
                        </Button>
                      </div>
                    </>
                  )}
                </Alert>
              )}
              {/* ── TRIAGEM DE CONFLITO (ADR-0025) ────────────────────────────
                  Só aparece DEPOIS de este leitor declarar o próprio conflito — o
                  servidor devolve `triagemConflito: null` antes disso, de propósito:
                  saber que a IA apontou alguém enviesaria a própria declaração.

                  Dois avisos DISTINTOS, e a distinção é o ponto:
                  · membro apontado → precisa de substituto;
                  · liberada sem análise → a proteção NÃO RODOU. "Sem apontamento"
                    aqui não é "sem conflito", e colapsar os dois num silêncio faria
                    a tela mentir por omissão. */}
              {data.triagemConflito?.membroDenunciadoId != null && (
                <Alert variant="warning" style={{ marginBottom: 12 }}>
                  <strong>{t('case_detail.triagem_membro_denunciado')}</strong>{' '}
                  {members?.find((m) => m.id === data.triagemConflito?.membroDenunciadoId)?.email ??
                    data.triagemConflito.membroDenunciadoId}
                  <br />
                  {t('case_detail.triagem_precisa_substituto')}
                </Alert>
              )}
              {data.triagemConflito?.liberadaSemAnalise === true && (
                <Alert variant="warning" style={{ marginBottom: 12 }}>
                  {t('case_detail.triagem_sem_analise')}
                </Alert>
              )}
              {/* Leitura por ISENÇÃO DO GESTOR — o colegiado NÃO fechou a atestação.
                  Avisar é obrigatório: sem isto o gestor lê o teor achando que o rito
                  seguiu o curso normal, e a diferença é justamente o que ele precisa
                  saber para decidir se assume o caso ou cobra os colegas. */}
              {data.teor?.isencaoGestor === true && (
                <Alert variant="warning" style={{ marginBottom: 12 }}>
                  {t('case_detail.teor_isencao_gestor', { faltam: data.teor.faltam })}
                </Alert>
              )}
              {/* 🔴 ASSUMIR SOZINHO SAIU DE DENTRO DO `decryptedJson &&` (2026-08-17).
                  Ele estava aninhado no bloco que só renderiza para quem JÁ decifrou o
                  caso, e isso criava um DEADLOCK com o gate colegiado: para assumir era
                  preciso ler, e para ler era preciso que os colegas declarassem — no
                  cenário exato em que o modo unipessoal existe para servir. Agora o
                  botão é irmão do bloco de leitura, não filho. */}
              {session?.user.roles.includes('admin_master') === true &&
                data.triagemConflito?.membroDenunciadoId !== session.user.id &&
                (data.assumidoUnipessoalPor ?? null) === null && (
                  <div className="case-acoes-acesso" style={{ marginBottom: 12 }}>
                    <Button
                      variant="ghost"
                      loading={assumindo}
                      onClick={() => void handleAssumirUnipessoal()}
                    >
                      {t('case_detail.assumir_unipessoal', { defaultValue: 'Assumir sozinho' })}
                    </Button>
                  </div>
                )}
              {/* P5 (ADR "Fatia 4 destravada") — propagação MANUAL de acesso.
                  Precisa de `decryptedJson`: propagar exige a DEK aberta.

                  🔴 E precisa de CONDUÇÃO UNIPESSOAL (operador, 2026-08-17). Antes o
                  botão aparecia para qualquer leitor, em qualquer caso, e isso gerava
                  três defeitos que o operador relatou juntos:

                  · em caso colegiado com todo mundo já embrulhado, o clique abria um
                    diálogo SEM candidatos — o botão prometia uma lista que não existia;
                  · o clique não mudava nada e o botão continuava lá, sem sinal de que
                    já havia sido acionado;
                  · o efeito REAL dele hoje é devolver o caso ao colegiado, e isso só
                    faz sentido enquanto alguém conduz sozinho.

                  Com o gate, o botão some assim que a condução termina — o que é mais
                  forte que desabilitar: não sobra controle inerte na tela.

                  🔴 MAS a condição é `condução OU há faltante`, e NÃO só condução — e a
                  diferença não é detalhe. O pedido literal do operador foi "só sob
                  condução unipessoal"; segui-lo ao pé da letra apagaria o ÚNICO caminho
                  de conceder acesso a um membro que entrou depois do submit num caso
                  COLEGIADO. Não há rede embaixo: `propagar-acesso.test.tsx` fixa que o
                  mount NÃO concede nada a ninguém ("zero rewrap automático", nos dois
                  modos) — o self-heal silencioso foi removido de propósito. Com só a
                  condução no gate, esse membro ficaria cego para sempre, sem erro em
                  lugar nenhum.

                  No cenário que motivou o pedido os dois se encontram: com todo mundo
                  já embrulhado no submit, `faltamAcesso` é vazio e o botão só aparece
                  sob condução — exatamente o comportamento pedido. */}
              {decryptedJson && (conduzoSozinho || faltamAcesso.length > 0) && (
                <div className="case-acoes-acesso" style={{ marginBottom: 12 }}>
                  <Button
                    variant="ghost"
                    onClick={() => void abrirPopupPropagacao()}
                    disabled={propagando}
                    title={t('case_detail.propagar_acesso_ajuda')}
                  >
                    {propagando ? '…' : t('case_detail.propagar_acesso_botao')}
                  </Button>
                  {/* 🔴 ASSUMIR SOZINHO VEIO PARA CÁ (operador, 2026-08-13), da barra
                      de ações do rito.

                      Motivo: não é ato do RITO — é ato de CONDUÇÃO, da mesma família
                      do "propagar acesso" (quem pode ler / quem conduz). Na barra ele
                      ficava entre Parecer e Decisão sem pertencer à sequência, e a
                      barra deixou de ensinar o processo por causa dele.

                      Os guards são os mesmos de antes, e nenhum foi afrouxado: só
                      `admin_master`, só enquanto ninguém assumiu. Some depois de
                      assumido — o selo no topo do caso passa a dizer o estado — e nunca
                      aparece para membro comum, porque o servidor recusa com 403 e
                      oferecer botão que o servidor recusa é o defeito que os gates de
                      fase existem para evitar. */}
                  {/* 🔴 O gestor DENUNCIADO não assume o próprio caso (ADR-0025) — a
                      condição vive agora no bloco acima, fora do `decryptedJson`. A trava
                      AUTORITATIVA continua no servidor (403); a da tela só evita oferecer
                      um botão que seria recusado. */}
                  {/* QUEM vai receber — nomeado antes de propagar (operador 14/08).
                      Antes o botão dizia só "propagar aos demais membros" e o membro
                      clicava sem saber a quem estava abrindo o teor de uma denúncia.
                      Nomear é o mínimo para que a decisão seja informada. */}
                  {faltamAcesso.length > 0 && (
                    <p className="propagar-lista" style={{ flexBasis: '100%' }}>
                      {t('case_detail.propagar_acesso_faltam')}{' '}
                      <strong>
                        {faltamAcesso
                          .map((idm) => members?.find((m) => m.id === idm)?.email ?? idm)
                          .join(', ')}
                      </strong>
                    </p>
                  )}
                  {popupCandidatos !== null && (
                    <Modal
                      title={t(
                        conduzoSozinho
                          ? 'case_detail.propagar_modal_titulo_delegar'
                          : 'case_detail.propagar_modal_titulo',
                      )}
                      titleId="modal-propagar"
                      onClose={() => {
                        setPopupCandidatos(null);
                      }}
                      actions={
                        <>
                          <Button
                            variant="ghost"
                            onClick={() => {
                              setPopupCandidatos(null);
                            }}
                          >
                            {t('case_detail.propagar_modal_cancelar')}
                          </Button>
                          <Button
                            variant="primary"
                            loading={propagando}
                            /* Nada selecionado E nada a encerrar ⇒ o botão não faria
                               nada. Sob condução, confirmar sem marcar ninguém é ato
                               legítimo: devolve o caso ao colegiado sem dar chave nova. */
                            disabled={selecionados.size === 0 && !conduzoSozinho}
                            onClick={() => void propagarAcesso()}
                          >
                            {t('case_detail.propagar_modal_confirmar')}
                          </Button>
                        </>
                      }
                    >
                      {/* ⚠️ O aviso do ENCERRAMENTO vem primeiro e é o mais forte: a
                          consequência maior deste diálogo não é dar leitura a alguém, é
                          o gestor deixar de conduzir sozinho — o caso volta a ser visível
                          ao comitê e o rito volta a exigir a votação de cada etapa. */}
                      {conduzoSozinho && (
                        <Alert variant="warning" style={{ marginBottom: 12 }}>
                          {t('case_detail.propagar_modal_encerra')}
                        </Alert>
                      )}
                      {/* "Marque os membros…" só faz sentido quando há o que marcar.
                          Sem candidatos, a instrução ficava pendurada acima de uma lista
                          que não existe — parte do mesmo defeito que o operador relatou. */}
                      {popupCandidatos.length > 0 && (
                        <p style={{ marginTop: 0, color: 'var(--text-secondary)' }}>
                          {t(
                            conduzoSozinho
                              ? 'case_detail.propagar_modal_ajuda_delegar'
                              : 'case_detail.propagar_modal_ajuda',
                          )}
                        </p>
                      )}
                      {/* A resposta ao título do diálogo. O RÓTULO muda com o modo, e é
                          essa troca que impede a tela de se contradizer: sob condução
                          ninguém além do condutor vê o caso, então "já leem" seria falso
                          — o que estas pessoas têm é a CHAVE, e o que confirmar devolve
                          a elas é a VISIBILIDADE. */}
                      {outrosComChave.length > 0 && (
                        <p className="propagar-lista" data-testid="propagar-ja-leem">
                          {t(
                            conduzoSozinho
                              ? 'case_detail.propagar_modal_voltarao_a_ver'
                              : 'case_detail.propagar_modal_ja_leem',
                          )}{' '}
                          <strong>
                            {outrosComChave
                              .map((idm) => members?.find((m) => m.id === idm)?.email ?? idm)
                              .join(', ')}
                          </strong>
                        </p>
                      )}
                      {popupCandidatos.length === 0 ? (
                        <p style={{ color: 'var(--text-secondary)' }}>
                          {t(
                            conduzoSozinho
                              ? 'case_detail.propagar_modal_sem_candidatos_conducao'
                              : 'case_detail.propagar_modal_sem_candidatos',
                          )}
                        </p>
                      ) : (
                        <ul className="propagar-escolha">
                          {popupCandidatos.map((cand) => (
                            <li key={cand.memberId}>
                              <label>
                                <input
                                  type="checkbox"
                                  style={{ accentColor: 'var(--primary)' }}
                                  checked={selecionados.has(cand.memberId)}
                                  onChange={(ev) => {
                                    setSelecionados((atual) => {
                                      const prox = new Set(atual);
                                      if (ev.target.checked) prox.add(cand.memberId);
                                      else prox.delete(cand.memberId);
                                      return prox;
                                    });
                                  }}
                                />
                                <span>
                                  {members?.find((m) => m.id === cand.memberId)?.email ??
                                    cand.memberId}
                                  {/* 🔴 QUEM PEDIU vem marcado (operador, 2026-08-19). A
                                      lista traz TODOS os que não têm a chave — pedintes e
                                      não-pedintes juntos. Sem a marca, conceder "a quem
                                      pediu" exigia decorar os nomes de outra tela, e a
                                      chance de liberar para a pessoa errada é real: o ato é
                                      irreversível (wrap entregue não volta). */}
                                  {data.solicitantesAcesso?.includes(cand.memberId) === true && (
                                    <strong
                                      className="cand-pediu"
                                      data-testid={`pediu-${cand.memberId}`}
                                    >
                                      {' '}
                                      {t('case_detail.propagar_pediu_acesso')}
                                    </strong>
                                  )}
                                  {/* Sob delegação a lista mistura quem já lê com quem
                                      ainda não — e a diferença importa: para os segundos
                                      o ato é irreversível (chave entregue não volta). */}
                                  {cand.precisaChave === true && (
                                    <em style={{ color: 'var(--text-secondary)' }}>
                                      {' '}
                                      {t('case_detail.propagar_modal_precisa_chave')}
                                    </em>
                                  )}
                                </span>
                              </label>
                            </li>
                          ))}
                        </ul>
                      )}
                    </Modal>
                  )}
                </div>
              )}
              {/* 🔴 FORA do bloco acima de propósito. O resultado da propagação precisa
                  sobreviver ao próprio efeito: propagar ENCERRA a condução unipessoal,
                  o bloco some, e um aviso aninhado nele desapareceria no mesmo instante
                  em que passaria a ser a única explicação do que acabou de acontecer.
                  Sai quando o gestor assume de novo (ver `handleAssumirUnipessoal`). */}
              {propagacaoMsg && (
                <Alert
                  variant={propagacaoMsg === 'falha' ? 'warning' : 'info'}
                  role="status"
                  aria-live="polite"
                  style={{ marginTop: 8, marginBottom: 12 }}
                >
                  {t(`case_detail.propagar_acesso_${propagacaoMsg}`)}
                </Alert>
              )}
              {/* QUEM acabou de receber. Também saiu do bloco da condução, e por um
                  motivo a mais: este estado é alimentado TAMBÉM pelo self-heal, que roda
                  sozinho no mount em caso COLEGIADO. Deixá-lo sob o gate de condução
                  unipessoal apagaria, em silêncio, o único aviso de que o self-heal deu
                  acesso a alguém — que é feedback que já existia antes deste diff. */}
              {propagadosAgora.length > 0 && (
                <p className="propagar-lista" style={{ marginBottom: 12 }}>
                  {t('case_detail.propagar_acesso_recebido')}{' '}
                  <strong>
                    {propagadosAgora
                      .map((idm) => members?.find((m) => m.id === idm)?.email ?? idm)
                      .join(', ')}
                  </strong>
                </p>
              )}
              {decryptedJson &&
                (() => {
                  // 02.8-06 — campos rotulados PT-BR; parse defensivo → fallback <pre>.
                  const conteudo = parseConteudo(decryptedJson);
                  if (!conteudo) {
                    return (
                      <div className="review-section">
                        <div className="review-label">{t('case_detail.conteudo_label')}</div>
                        <pre
                          className="review-value"
                          style={{
                            whiteSpace: 'pre-wrap',
                            fontFamily: 'Courier New, monospace',
                            fontSize: '0.85rem',
                          }}
                        >
                          {decryptedJson}
                        </pre>
                      </div>
                    );
                  }
                  const campos = [
                    ['campo_descricao', conteudo.descricao],
                    ['campo_quando', conteudo.quando],
                    ['campo_onde', conteudo.onde],
                    ['campo_envolvidos', conteudo.envolvidos],
                  ] as const;
                  return (
                    <div>
                      {campos.map(([key, val]) =>
                        typeof val === 'string' && val.trim() !== '' ? (
                          <div className="review-section" key={key}>
                            <div className="review-label">{t(`case_detail.${key}`)}</div>
                            <div className="review-value" style={{ whiteSpace: 'pre-wrap' }}>
                              {val}
                            </div>
                          </div>
                        ) : null,
                      )}
                      {typeof conteudo.identificacaoVoluntaria === 'boolean' && (
                        <div className="review-section">
                          <div className="review-label">
                            {t('case_detail.identificacao_voluntaria')}
                          </div>
                          <div className="review-value">
                            {conteudo.identificacaoVoluntaria
                              ? t('case_detail.identificacao_sim')
                              : t('case_detail.identificacao_nao')}
                          </div>
                        </div>
                      )}
                      {typeof conteudo.policyVersion === 'string' &&
                        typeof conteudo.policyAcceptedAt === 'number' && (
                          <p
                            style={{
                              marginTop: 12,
                              fontSize: '0.72rem',
                              color: 'var(--text-secondary)',
                              fontStyle: 'italic',
                            }}
                          >
                            {t('case_detail.metadata_aceite', {
                              version: conteudo.policyVersion,
                              date: new Date(conteudo.policyAcceptedAt).toLocaleString('pt-BR'),
                            })}
                          </p>
                        )}
                    </div>
                  );
                })()}
              {/* D5 — informação complementar do denunciante. Fica na aba DESCRIÇÃO
                  (não na "Mensagens") porque `/api/status/info-complementar` NÃO é
                  gateado por COMITE_MESSAGING_ENABLED: com o canal desligado ela
                  ainda chega, e a aba "Mensagens" nem existe. Ver o cabeçalho do
                  InfoComplementarPanel. */}
              <InfoComplementarPanel itens={decryptedInfo} />
              {/* P10 (ADR-0018) — Identificação voluntária (só aparece se o
                  denunciante se identificou; decifrada no mesmo withCaseDek). */}
              {decryptedIdentidade && (
                <div
                  className="review-section"
                  style={{
                    marginTop: 16,
                    paddingTop: 12,
                    borderTop: '1px solid var(--border, #334155)',
                  }}
                >
                  <div className="review-label">
                    <strong>{t('case_detail.identidade_titulo')}</strong>
                  </div>
                  {decryptedIdentidade.nome && (
                    <p>
                      {t('case_detail.identidade_nome')}: {decryptedIdentidade.nome}
                    </p>
                  )}
                  {decryptedIdentidade.contato && (
                    <p>
                      {t('case_detail.identidade_contato')}: {decryptedIdentidade.contato}
                    </p>
                  )}
                  {decryptedIdentidade.cpf && (
                    <p>
                      {t('case_detail.identidade_cpf')}: {decryptedIdentidade.cpf}
                    </p>
                  )}
                </div>
              )}
              {!decryptedJson && !decryptBorda && (
                <Alert variant="info" role="status">
                  {t('case_detail.encrypted_loading')}
                </Alert>
              )}
              <div className="review-section">
                <div className="review-label">{t('case_detail.criado_em')}</div>
                {/* 🔴 Mostrava `data.createdWeek` — a STRING DE SEMANA ("2026-W33"),
                    sem nem olhar o `openedAt`. Foi o que o operador viu continuar
                    semanal depois da mudança de 13/08: eu havia trocado o formatador
                    do RitoBoardPage e do histórico, e o rótulo "Aberto em" que ele
                    tinha na frente lia outro campo.

                    `state.openedAt` existe em todos os kinds MENOS `Encerrada`, que
                    perde o campo — daí o fallback para a semana, que é o que
                    realmente se sabe ali. Fallback declarado, não silencioso. */}
                <div className="review-value">
                  {'openedAt' in data.state && typeof data.state.openedAt === 'number'
                    ? dataHora(data.state.openedAt, null, { segundos: true })
                    : data.createdWeek}
                </div>
              </div>
              <div className="review-section">
                <div className="review-label">
                  {t('case_detail.anexos_count', { count: data.attachments.length })}
                </div>
              </div>
            </div>
          )}

          {tab === 'pareceres' && (
            <div
              role="tabpanel"
              id={tabPanelId('pareceres')}
              aria-labelledby={tabId('pareceres')}
              tabIndex={0}
            >
              {/* Sprint 1.1b — pareceres reais lidos de state.pareceres, justificativa
                  decifrada client-side com o mesmo DEK da descrição (AAD=caseId).
                  Lookup memberId→nome humanizado pendente — Sprint 2/3 wira endpoint
                  /api/comite/users (privacy invariant D-212 exige ajuste). */}
              {(() => {
                const pareceres = extractPareceres(data.state);
                if (pareceres.length === 0) {
                  /* A copy dependia de um botão que agora SOME fora de fase: em
                     Triagem, "Use o botão 'Parecer' acima" mandaria fazer algo
                     impossível. Pergunta à MESMA fonte que decide o botão — se ele
                     é USÁVEL, aponta para ele; se não, diz quando a votação abre.
                     Aba de leitura permanece visível (decisão do operador 11/08).

                     🔴 A condição é `!desabilitado`, NÃO `mostrar`. Quando o botão
                     fora de fase passou a aparecer desabilitado em vez de sumir,
                     `mostrar` virou sempre `true` e esta copy voltou a mandar usar
                     um botão que existe e não clica. Foi o teste do estado vazio que
                     pegou — o que a mudança de comportamento quebrou não foi o
                     botão, foi a frase que falava dele. */
                  const podeEmitir = (() => {
                    const s = situacaoNaTela(data.capabilities?.acoes, 'emitir_parecer');
                    return s.mostrar && !s.desabilitado;
                  })();
                  return (
                    <p style={{ color: 'var(--color-text-secondary)' }}>
                      {podeEmitir
                        ? t('case_detail.pareceres_vazio_agora', {
                            defaultValue:
                              'Nenhum parecer emitido ainda. Use o botão "Parecer" acima.',
                          })
                        : t('case_detail.pareceres_vazio_fora_de_fase', {
                            defaultValue:
                              'Nenhum parecer emitido ainda. A votação abre quando a fase de oitivas for encerrada.',
                          })}
                    </p>
                  );
                }
                return (
                  <div style={{ marginBottom: 24 }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                        marginBottom: 12,
                      }}
                    >
                      <h3 style={{ margin: 0, fontSize: '1rem' }}>Pareceres do comitê</h3>
                      <span style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>
                        {pareceres.length} parecer(es) emitido(s)
                      </span>
                    </div>
                    {pareceres.map((p) => {
                      const isSelf = session?.user.id === p.memberId;
                      // E-MAIL, com fallback no id CRU (nunca truncado): a lista de
                      // membros pode não ter carregado, e id cortado não identifica
                      // ninguém — era preciso cruzar à mão para saber quem opinou.
                      const displayId = `${members?.find((m) => m.id === p.memberId)?.email ?? p.memberId}${isSelf ? ' (você)' : ''}`;
                      const justificativa = decryptedPareceres.get(p.memberId);
                      return (
                        <Card key={p.memberId} padding="sm" style={{ marginBottom: 10 }}>
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              marginBottom: 10,
                              flexWrap: 'wrap',
                              gap: 6,
                            }}
                          >
                            <span
                              style={{
                                fontWeight: 'var(--fw-bold)',
                                fontSize: '0.9rem',
                                fontFamily: 'var(--font-mono)',
                              }}
                            >
                              {displayId}
                            </span>
                            <Badge tone={votoTone(p.voto)}>
                              {t(`parecer.votos.${p.voto}`, { defaultValue: p.voto })}
                            </Badge>
                          </div>
                          <p
                            style={{
                              fontSize: '0.92rem',
                              lineHeight: 1.55,
                              margin: 0,
                              color: justificativa
                                ? 'var(--text-primary)'
                                : 'var(--text-secondary)',
                              fontStyle: justificativa ? 'normal' : 'italic',
                            }}
                          >
                            {justificativa ?? 'Decifrando justificativa…'}
                          </p>
                          <div
                            style={{
                              marginTop: 8,
                              fontSize: '0.72rem',
                              color: 'var(--text-secondary)',
                            }}
                          >
                            Emitido em {new Date(p.emittedAt).toLocaleString('pt-BR')}
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                );
              })()}

              {/* ADR-0003 — IA-Analista parecer com textoDenuncia placeholder (Phase 4 wire-up spike).
                  AnalistaContext estruturalmente sem humanVerdicts (D-87) —
                  consultar IA não enviesa voto humano e vice-versa. */}
              <div
                className="review-section"
                style={{
                  marginTop: 24,
                  paddingTop: 20,
                  borderTop: '1px solid var(--surface-cream-deep, #e6dfd2)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 12,
                  }}
                >
                  <div>
                    <div className="review-label" style={{ fontSize: '1rem', fontWeight: 600 }}>
                      {t('parecer_ia.title')}
                    </div>
                    <p
                      style={{
                        fontSize: '0.85rem',
                        color: 'var(--color-text-secondary)',
                        margin: '4px 0 0',
                      }}
                    >
                      {t('parecer_ia.subtitle')}
                    </p>
                  </div>
                  {!iaParecer && (
                    <Button
                      variant="ghost"
                      size="sm"
                      loading={iaLoading}
                      onClick={() => void handleSolicitarParecerIA()}
                    >
                      {iaLoading ? t('parecer_ia.loading') : t('parecer_ia.solicitar')}
                    </Button>
                  )}
                </div>

                {iaError && (
                  <Alert variant="error">
                    {t(`parecer_ia.errors.${iaError}`, {
                      defaultValue: t('parecer_ia.errors.ia_failed'),
                    })}
                  </Alert>
                )}

                {iaParecer && (
                  <div className="sintese-suggestion" style={{ marginTop: 8 }}>
                    <IaSimuladaBadge aiMode={aiMode} />
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 12,
                        flexWrap: 'wrap',
                        gap: 8,
                      }}
                    >
                      <Badge tone={votoTone(iaParecer.recommendation as Voto)} solid>
                        {t(`parecer.votos.${iaParecer.recommendation}`, {
                          defaultValue: iaParecer.recommendation,
                        })}
                      </Badge>
                      <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                        {t('parecer_ia.confidence')}:{' '}
                        <strong>{(iaParecer.confidence * 100).toFixed(0)}%</strong>
                      </div>
                    </div>

                    <div className="review-label" style={{ fontSize: '0.85rem', marginBottom: 4 }}>
                      {t('parecer_ia.justificativa')}
                    </div>
                    <p
                      style={{
                        fontSize: '0.95rem',
                        lineHeight: 1.6,
                        margin: '0 0 16px',
                      }}
                    >
                      {iaParecer.justificativa}
                    </p>

                    {iaParecer.citations.length > 0 && (
                      <>
                        <div
                          className="review-label"
                          style={{ fontSize: '0.85rem', marginBottom: 8 }}
                        >
                          {t('parecer_ia.citations')}
                        </div>
                        <ul style={{ paddingLeft: 20, margin: 0 }}>
                          {iaParecer.citations.map((cite, idx) => (
                            <li key={idx} style={{ marginBottom: 8, fontSize: '0.9rem' }}>
                              <strong>{cite.source}:</strong>{' '}
                              <span style={{ fontStyle: 'italic' }}>"{cite.text}"</span>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}

                    <p
                      style={{
                        marginTop: 16,
                        fontSize: '0.75rem',
                        color: 'var(--color-text-secondary)',
                        fontStyle: 'italic',
                      }}
                    >
                      {t('parecer_ia.disclaimer')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {tab === 'votos' && (
            <div
              role="tabpanel"
              id={tabPanelId('votos')}
              aria-labelledby={tabId('votos')}
              tabIndex={0}
            >
              {(() => {
                const pareceres = extractPareceres(data.state);
                const humanRows: HumanVerdictRow[] = pareceres.map((p) => {
                  const justificativa = decryptedPareceres.get(p.memberId);
                  const isSelf = session?.user.id === p.memberId;
                  const memberLabel = `${members?.find((m) => m.id === p.memberId)?.email ?? p.memberId}${isSelf ? ' (você)' : ''}`;
                  const truncated = justificativa
                    ? justificativa.length > 120
                      ? `${justificativa.slice(0, 120)}…`
                      : justificativa
                    : '(decifrando…)';
                  return {
                    memberId: p.memberId,
                    memberLabel,
                    voto: p.voto,
                    justificativaTruncada: truncated,
                  };
                });
                const analistaVoto =
                  (iaParecer?.recommendation as
                    | 'procedente'
                    | 'improcedente'
                    | 'improcedente_insuficiencia'
                    | undefined) ?? null;
                return (
                  <VotosComiteCard
                    humanRows={humanRows}
                    analistaParecer={iaParecer?.justificativa ?? null}
                    analistaVoto={analistaVoto}
                  />
                );
              })()}
            </div>
          )}

          {tab === 'oitivas' && (
            <div
              role="tabpanel"
              id={tabPanelId('oitivas')}
              aria-labelledby={tabId('oitivas')}
              tabIndex={0}
            >
              <OitivaPanel
                caseId={caseId}
                categoria={data.categoria}
                state={data.state}
                onMutate={() => void load()}
                caseCrypto={resolveCaseDekAccess(data, session?.user.id ?? '')}
              />
            </div>
          )}

          {tab === 'plano' && (
            <div
              role="tabpanel"
              id={tabPanelId('plano')}
              aria-labelledby={tabId('plano')}
              tabIndex={0}
            >
              {/* `planos={data.planos ?? []}`: sob `exactOptionalPropertyTypes`, passar
                  `undefined` explícito a um `planos?:` é erro de tipo — e o default
                  vazio é o comportamento certo, porque projeção antiga vira lista
                  vazia em vez de quebrar a aba. */}
              <PlanoPanel
                caseId={data.caseId}
                categoria={data.categoria}
                createdWeek={data.createdWeek}
                state={data.state}
                planos={data.planos ?? []}
                caseCrypto={resolveCaseDekAccess(data, session?.user.id ?? '')}
                members={members}
                onGerado={() => void load()}
              />
            </div>
          )}

          {tab === 'relatorio' && (
            <div
              role="tabpanel"
              id={tabPanelId('relatorio')}
              aria-labelledby={tabId('relatorio')}
              tabIndex={0}
            >
              <RelatorioPanel
                caseId={data.caseId}
                categoria={data.categoria}
                state={data.state}
                caseCrypto={resolveCaseDekAccess(data, session?.user.id ?? '')}
              />
            </div>
          )}

          {tab === 'mensagens' && (
            <div
              role="tabpanel"
              id={tabPanelId('mensagens')}
              aria-labelledby={tabId('mensagens')}
              tabIndex={0}
            >
              <MessageComposer
                caseId={data.caseId}
                caseCrypto={resolveCaseDekAccess(data, session?.user.id ?? '')}
                messages={data.mockMessages}
                attachments={data.attachments}
                onSent={() => void load()}
              />
            </div>
          )}

          {tab === 'audit' && (
            <div
              role="tabpanel"
              id={tabPanelId('audit')}
              aria-labelledby={tabId('audit')}
              tabIndex={0}
            >
              {/* Registros de classificação ANTES da timeline geral: a pergunta
                  "como este caso foi classificado, e por quem, ao longo do
                  tempo" é a mais frequente do rito, e ficava diluída entre todos
                  os outros eventos do histórico. */}
              <ClassificacoesPanel caseId={caseId} members={members} />
              <div style={{ marginTop: 24 }}>
                <HistoricoPanel caseId={caseId} members={members} />
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
